import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import CoachModeAuthModal from './CoachModeAuthModal';
import ExerciseSelectorGrid from './ExerciseSelectorGrid';
import { useLastSessionsHints } from './LastSessionsHint';
import ExerciseHeader from './intra/ExerciseHeader';
import ExerciseHistoryModal from '../ExerciseHistoryModal';
import WorkoutDetailsModal from './WorkoutDetailsModal';
import SetTable from './intra/SetTable';
import { resolveSetTargets } from '../../utils/progression';
import { estimateWorkoutSeconds, estimateRemainingSeconds, adaptiveRemainingSeconds, formatDuration } from '../../utils/workoutDuration';
import { saveDraft, getDraft, markSynced, clearDraft, isDraftRecoverable, getStartAnchor, saveStartAnchor, clearStartAnchor } from '../../utils/workoutDraftStore';
import {
  buildExercisesToSave,
  getAuthHeaders,
  getRpeLabel,
  getRestTimeColor,
  getRestColorClasses,
} from './intra/workoutHelpers';
import {
  LastSetRpeModal,
  SessionClosingNoticeModal,
  SessionRatingModal,
  WorkoutSummaryModal,
  ExitConfirmationModal,
  PrCelebrationToast,
} from './intra/WorkoutModals';
import { useCoachMode } from './intra/useCoachMode';
import { synthesizeTimerSound, getTimerSoundName } from '../../utils/timerSounds';
// Note: getTranslatedExercise is no longer needed - all exercises have pre-stored _es fields

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hard cap for a single session's tracked duration. Guards against stale start
// anchors: a flexible routine left "En curso" for days and then resumed/finished
// would otherwise compute wall-clock (now − started_at) and save tens of
// thousands of minutes. No real session exceeds this, so clamp at save + display.
const MAX_SESSION_SECONDS = 4 * 60 * 60; // 4 hours

const ActiveWorkoutInterface = ({ 
  workout, 
  workoutData, 
  setWorkoutData, 
  currentExerciseIndex, 
  setCurrentExerciseIndex, 
  onFinishWorkout, 
  onExit,
  onCancelWorkout, 
  language,
  t 
}) => {
  const currentExercise = workout.exercises[currentExerciseIndex];
  const [currentSet, setCurrentSet] = useState(1);
  const [setData, setSetData] = useState({
    reps: '',
    weight: '',
    rest_time: '120',  // Default 2 minutes rest
    rpe: ''
  });
  const [isResting, setIsResting] = useState(false);
  // ARCHITECTURE FIX (rest-timer):
  //   restTimer  = REAL elapsed rest time (stopwatch counting up from 0). Never
  //                reset by RPE. Only resets when a new rest starts or skipped.
  //   restTarget = recommended target duration (seconds). RPE updates this
  //                only — never the elapsed timer.
  const [restTimer, setRestTimer] = useState(0);
  const [restTarget, setRestTarget] = useState(120);
  const [showExerciseDetails, setShowExerciseDetails] = useState(false);
  const [fullExerciseData, setFullExerciseData] = useState(null);
  
  // Session rating and exercise feedback
  const [sessionRating, setSessionRating] = useState(0);
  const [sessionNotes, setSessionNotes] = useState('');
  const [exerciseFeedback, setExerciseFeedback] = useState({}); // {exerciseId: 'favorite' | 'dislike' | null}
  const [pendingFinishData, setPendingFinishData] = useState(null);
  
  // =============================================
  // CARDIO MODE: Timer-based execution for cardio exercises
  // =============================================
  const [cardioTimer, setCardioTimer] = useState(0); // elapsed seconds
  const [isCardioTimerRunning, setIsCardioTimerRunning] = useState(false);
  const [cardioTargetTime, setCardioTargetTime] = useState(60); // target in seconds
  const [cardioRounds, setCardioRounds] = useState([]); // completed rounds: [{time: 60, completed_at: ...}]
  const [cardioTargetHit, setCardioTargetHit] = useState(false); // timed set reached its target and auto-stopped, awaiting confirm
  // "+ info" popup: shows the finished-workout summary for a past PR / last session
  const [dayWorkout, setDayWorkout] = useState(null);
  const [dayWorkoutLoading, setDayWorkoutLoading] = useState(false);
  // "Ver progreso" — full progression chart modal for the current exercise.
  const [showProgress, setShowProgress] = useState(false);
  const cardioTimerRef = useRef(null);
  const cardioStartRef = useRef(null); // wall-clock anchor (ms) for the cardio timer
  
  // 4-STEP SESSION COMPLETION FLOW:
  // Step 1: Last set RPE (technical effort for final set)
  const [showLastSetRpeModal, setShowLastSetRpeModal] = useState(false);
  const [pendingLastSetRpe, setPendingLastSetRpe] = useState(null);
  // Step 2: Closing notice (informational transition)
  const [showClosingNoticeModal, setShowClosingNoticeModal] = useState(false);
  // Step 3: Session rating (global experience rating)
  const [showSessionRatingModal, setShowSessionRatingModal] = useState(false);
  // Step 4: Workout summary (final metrics display)
  const [showWorkoutSummary, setShowWorkoutSummary] = useState(false);
  const [workoutSummaryData, setWorkoutSummaryData] = useState(null);
  
  // PART 4: Exercise notes/observations per exercise
  const [exerciseNotes, setExerciseNotes] = useState({}); // {exerciseIndex: 'note text'}
  
  // PART 5 FIX: Track previous exercise to detect changes and preload images
  const [imageCache, setImageCache] = useState({}); // {exerciseId: imageUrl}
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  
  const [supersetState, setSupersetState] = useState({
    isInSuperset: false,
    firstExerciseIndex: null,
    secondExerciseIndex: null,
    onSecondExercise: false,
    currentGroup: null
  });
  
  // ERP Phase 4.7: Personal Records display
  const [exercisePRs, setExercisePRs] = useState({}); // {exercise_id: {weight, reps, date}}
  
  // P0-1: Last-time hints — last 3 sessions per exercise + PR + suggestion
  const allExerciseIdsForHints = useMemo(() => {
    return (workoutData || []).map((ex) => ex?.exercise_id).filter(Boolean);
  }, [workoutData]);
  const { data: lastSessionsHints } = useLastSessionsHints(
    workout?.client_id && workout.client_id !== 'template' ? workout.client_id : null,
    allExerciseIdsForHints,
    3
  );
  
  // Client exercise usage history for weight autofill
  const [clientExerciseUsage, setClientExerciseUsage] = useState({}); // {exercise_id: {last_weight, last_reps}}
  
  // PART 2 FIX: Single source of truth for active exercise
  // This ref is updated synchronously on every exercise change, preventing stale-index bugs
  const activeExerciseIdRef = useRef(currentExercise?.exercise_id || null);
  
  // Pending navigation after rest (for rest after last set of exercise)
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // RPE for the LAST COMPLETED SET (entered during rest timer)
  const [lastCompletedSetRpe, setLastCompletedSetRpe] = useState(null);
  // Track which set was last completed for RPE retroactive save
  const lastCompletedSetRef = useRef({ exerciseIndex: null, setIndex: null });

  // Real-time PR celebration — fires when a logged set beats the exercise's
  // historical best during the live session. { exerciseName, weight, reps, setNumber }
  const [prCelebration, setPrCelebration] = useState(null);
  // Highest mark already celebrated per exercise_id, so we only fire when
  // strictly beaten again (avoids repeat toasts on equal/lower sets).
  const celebratedPrRef = useRef({});
  const prCelebrationTimerRef = useRef(null);
  
  // Training time tracking
  // Durable start anchor: survives app kill / reload / remount (Android/PWA
  // backgrounding). Priority: persisted local anchor → server started_at → now.
  // Without this, a remount reset the timer to 0 and saved a 0-minute workout.
  const [workoutStartTime] = useState(() => {
    const wid = workout?.id;
    const persisted = getStartAnchor(wid);
    if (persisted) {
      const d = new Date(persisted);
      if (!isNaN(d.getTime())) return d;
    }
    if (workout?.started_at) {
      const d = new Date(workout.started_at);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  });
  const [totalRestTime, setTotalRestTime] = useState(0);
  const [currentRestStartTime, setCurrentRestStartTime] = useState(null);
  const currentRestStartTimeRef = useRef(null); // Ref mirror to avoid stale closures
  const [elapsedTime, setElapsedTime] = useState(0); // Live elapsed timer
  
  // =============================================
  // SAFARI/TABLET FIX: Initialization guard
  // Prevents autosave and other effects from firing before component is ready
  // =============================================
  const [isInitialized, setIsInitialized] = useState(false);
  const isMountedRef = useRef(true);

  // Remote coach edits (from the Live Board on the trainer's own device): a
  // lightweight poll watches the workout's structure revision. When the coach
  // changes something, we DON'T jump the screen — we surface a manual
  // "refresh" banner the client can tap when ready.
  const [coachUpdateAvailable, setCoachUpdateAvailable] = useState(false);
  const [applyingCoachUpdate, setApplyingCoachUpdate] = useState(false);
  const [coachUpdateIsExec, setCoachUpdateIsExec] = useState(false);
  const lastSeenRevRef = useRef(null);
  const lastSeenExecRef = useRef(null);
  const execChangedRef = useRef(false);
  
  // Mark component as mounted and initialized after first render cycle
  useEffect(() => {
    isMountedRef.current = true;
    
    // Delay initialization to ensure all state is settled
    // This is critical for Safari which has slower JS execution
    const initTimer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsInitialized(true);
        console.log('[INIT] ActiveWorkoutInterface initialized');
      }
    }, 500); // 500ms delay for Safari
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(initTimer);
    };
  }, []);
  
  // =============================================
  // P0 FIX: NAVIGATION PREVENTION (Data Loss Protection)
  // Prevent accidental navigation away from active workout session
  // =============================================
  const [isDirty, setIsDirty] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [pendingExitAction, setPendingExitAction] = useState(null);
  
  // Track dirty state when workout data changes
  useEffect(() => {
    // Consider dirty if any sets have been logged
    const hasLoggedSets = workoutData.some(ex => ex.actual_sets && ex.actual_sets.length > 0);
    setIsDirty(hasLoggedSets);
  }, [workoutData]);
  
  // Browser beforeunload event - handles refresh, close tab, browser back
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        const message = 'You have unsaved changes. If you leave this training session now, your data will be lost.';
        e.preventDefault();
        e.returnValue = message; // Required for Chrome
        return message; // Required for other browsers
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);
  
  // Handle browser back button via popstate
  useEffect(() => {
    const handlePopState = (e) => {
      if (isDirty) {
        // Push the state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        setShowExitConfirmation(true);
        setPendingExitAction('back');
      }
    };
    
    // Push initial state to handle back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty]);
  
  // Handle confirmed exit
  const handleConfirmExit = () => {
    setIsDirty(false); // Clear dirty state to allow navigation
    setShowExitConfirmation(false);
    
    if (pendingExitAction === 'back') {
      window.history.back();
    } else if (pendingExitAction === 'cancel') {
      onCancelWorkout();
    }
    setPendingExitAction(null);
  };
  
  // Handle cancel exit (stay in workout)
  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setPendingExitAction(null);
  };
  
  // Intercept cancel workout button
  const handleCancelWorkoutClick = () => {
    if (isDirty) {
      setShowExitConfirmation(true);
      setPendingExitAction('cancel');
    } else {
      onCancelWorkout();
    }
  };

  // =============================================
  // COACH MODE SYSTEM - Structural modifications
  // Session-scoped elevation for coaches/trainers
  // =============================================
  const [isCoachMode, setIsCoachMode] = useState(false);
  const [showCoachModeAuth, setShowCoachModeAuth] = useState(false);
  const [showExercisePicker, setShowExercisePicker] = useState(false);
  const [exercisePickerMode, setExercisePickerMode] = useState('add'); // 'add' | 'replace'
  const [replaceExerciseIndex, setReplaceExerciseIndex] = useState(null);
  const [allExercisesCatalog, setAllExercisesCatalog] = useState([]); // Full exercise library for selector

  // STRUCTURAL FIX: Memoize excludeExerciseIds to prevent re-renders from timer updates
  // This ensures the ExerciseSelectorGrid doesn't re-fetch on every parent render
  const excludeExerciseIds = useMemo(() => {
    return new Set(workout.exercises.map(ex => ex.exercise_id));
  }, [workout.exercises]);

  // Filtered exercise catalog (exclude already-in-workout exercises)
  const filteredExerciseCatalog = useMemo(() => {
    return allExercisesCatalog.filter(ex => !excludeExerciseIds.has(ex.id));
  }, [allExercisesCatalog, excludeExerciseIds]);

  // Fetch full exercise catalog when coach mode picker opens
  const {
    coachModeError,
    setCoachModeError,
    coachModeAxios,
    openExercisePicker,
    handleCloseExercisePicker,
    verifyCoachPin,
    activateCoachMode,
    handleCoachAddExercise,
    handleCoachReplaceExercise,
    handleCoachRemoveExercise,
    handleMoveExercise,
    handleCoachUpdateTargets,
    handleCoachApplyToTemplate,
  } = useCoachMode({
    isCoachMode,
    setIsCoachMode,
    workout,
    workoutData,
    setWorkoutData,
    currentExerciseIndex,
    setCurrentExerciseIndex,
    allExercisesCatalog,
    setAllExercisesCatalog,
    clientExerciseUsage,
    setClientExerciseUsage,
    replaceExerciseIndex,
    setReplaceExerciseIndex,
    setExercisePickerMode,
    setShowExercisePicker,
  });

  // =============================================
  // P0: AUTOSAVE SYSTEM - Automatic background saving
  // OPTIMIZATION: Only save when state has actually changed
  // =============================================
  const [lastAutosave, setLastAutosave] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [autosaveError, setAutosaveError] = useState(null);
  // P0.1 Offline buffer: subtle sync status ('synced' | 'pending') + recovery toast
  const [syncState, setSyncState] = useState('synced');
  const [showRecoveryToast, setShowRecoveryToast] = useState(false);
  const recoveryDoneRef = useRef(false);
  const autosaveTimeoutRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false); // Track if there are changes to save
  const lastSavedDataRef = useRef(null); // Track last saved state to detect changes
  const AUTOSAVE_DELAY = 3000; // 3 seconds debounce

  // Early-finish ("Terminar antes") routes through the SAME 4-step end-of-
  // workout survey as a normal completion:
  //   STEP 1  LastSetRpeModal       (only if the most-recent logged set has no RPE)
  //   STEP 2  SessionClosingNotice  (acts as the "you're about to finish" warning)
  //   STEP 3  SessionRatingModal    (global 1–10 experience rating)
  //   STEP 4  WorkoutSummaryModal   (final overview → onFinishWorkout)
  //
  // For the "no sets logged at all" emergency case we skip the RPE step
  // (nothing to rate) but still show the closing notice so the user has
  // an explicit "Continuar →" gate before the workout is dismissed.
  const [showEarlyFinishConfirm, setShowEarlyFinishConfirm] = useState(false);

  const beginFinishFlow = useCallback(() => {
    if (!workoutStartTime) {
      // Defensive: no start time → fall back to the legacy behaviour rather
      // than crash. Shouldn't happen in practice (start is stamped on mount).
      onFinishWorkout?.();
      onExit?.();
      return;
    }
    const totalDuration = Math.min(Math.floor((new Date() - workoutStartTime) / 1000), MAX_SESSION_SECONDS);
    const clampedRestTime = Math.min(totalRestTime, totalDuration);
    const effectiveTrainingTime = Math.max(0, totalDuration - clampedRestTime);
    setPendingFinishData({ totalDuration, totalRestTime: clampedRestTime, effectiveTrainingTime });

    // Find the most-recently-logged set across ALL exercises so RPE is
    // attached to whatever the client actually trained last — not blindly
    // to the "current" exercise (which the user may have skipped past).
    let latestExIdx = -1;
    let latestSetIdx = -1;
    let latestTs = -1;
    (workoutData || []).forEach((ex, exIdx) => {
      (ex?.actual_sets || []).forEach((s, sIdx) => {
        const ts = s?.completed_at ? new Date(s.completed_at).getTime() : 0;
        if (ts >= latestTs) {
          latestTs = ts;
          latestExIdx = exIdx;
          latestSetIdx = sIdx;
        }
      });
    });

    if (latestExIdx === -1) {
      // No sets logged at all — skip RPE, jump to the closing notice.
      setShowClosingNoticeModal(true);
      return;
    }

    const lastSet = workoutData[latestExIdx].actual_sets[latestSetIdx];
    if (!lastSet?.rpe) {
      const ex = workoutData[latestExIdx];
      setPendingLastSetRpe({
        exerciseIndex: latestExIdx,
        setIndex: latestSetIdx,
        exerciseName: ex?.exercise_name_es || ex?.exercise_name || 'Última serie',
      });
      setShowLastSetRpeModal(true);
    } else {
      setShowClosingNoticeModal(true);
    }
  }, [workoutData, workoutStartTime, totalRestTime, onFinishWorkout, onExit]);

  const handleEarlyFinishClick = useCallback(() => {
    setShowEarlyFinishConfirm(true);
  }, []);

  const confirmEarlyFinish = useCallback(() => {
    setShowEarlyFinishConfirm(false);
    beginFinishFlow();
  }, [beginFinishFlow]);

  // LIVE BOARD FIX: Immediate save that accepts data directly (bypasses stale state + debounce)
  // Called on set completion so the Live Board sees the update within 2 seconds
  const saveProgressNow = useCallback(async (dataToSave) => {
    if (!workout?.id || !isMountedRef.current) return;
    
    try {
      const exercisesToSave = buildExercisesToSave(dataToSave, exerciseNotes);
      
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      // CRITICAL: When Coach Mode is active, attach the X-Coach-Mode header so
      // the backend will accept structural changes (is_superset / superset_group)
      // even when the authenticated user is the CLIENT — which is the normal
      // case when a trainer toggles superset on the client's own tablet.
      // Without this, the save-progress guard strips superset flags by role
      // and the Live Board never sees the trainer's grouping.
      const headers = { ...getAuthHeaders() };
      if (isCoachMode) headers['X-Coach-Mode'] = 'true';
      await axios.put(`${API}/workouts/${workout.id}/save-progress`, exercisesToSave, { headers });
      
      if (isMountedRef.current) {
        setLastAutosave(new Date());
        setIsDirty(false);
        hasUnsavedChangesRef.current = false;
        lastSavedDataRef.current = JSON.stringify(exercisesToSave);
        markSynced(workout.id);
        setSyncState('synced');
        console.log('[SAVE-NOW] Set completion saved immediately at', new Date().toLocaleTimeString());
      }
    } catch (error) {
      console.error('[SAVE-NOW] Immediate save failed, debounced autosave will retry:', error);
      // Don't block — the debounced autosave will catch it. The local draft
      // (written on every change) keeps the data safe until the next flush.
      hasUnsavedChangesRef.current = true;
      if (isMountedRef.current) setSyncState('pending');
    }
  }, [workout?.id, exerciseNotes, isCoachMode]);
  
  // Coach Mode: Edit a completed (done) set on the current exercise.
  // Lets the trainer fix a value the client logged wrongly without
  // rolling back the workout state (rest timer, set counter, etc.).
  // Persists immediately via saveProgressNow so the Live Board and
  // history reflect the correction within ~2s.
  const handleEditDoneSet = useCallback((exerciseIndex, setIndex, patch) => {
    if (!isCoachMode) return;
    const updated = [...workoutData];
    const ex = updated[exerciseIndex];
    if (!ex || !Array.isArray(ex.actual_sets) || !ex.actual_sets[setIndex]) return;
    const nextSets = ex.actual_sets.map((s, i) =>
      i === setIndex ? { ...s, ...patch } : s
    );
    updated[exerciseIndex] = { ...ex, actual_sets: nextSets };
    setWorkoutData(updated);
    // Mirror onto the parent workout prop so the dashboard reflects the
    // edit if the user closes the active interface before autosave fires.
    if (workout?.exercises?.[exerciseIndex]) {
      workout.exercises[exerciseIndex].actual_sets = nextSets;
    }
    saveProgressNow(updated);
  }, [isCoachMode, workoutData, setWorkoutData, workout, saveProgressNow]);

  // Coach Mode: Toggle superset on exercise
  // STALE CLOSURE FIX: Use saveProgressNow(updated) instead of triggerAutosave()
  const handleToggleSuperset = useCallback((exerciseIndex) => {
    if (!isCoachMode) return;
    const updated = [...workoutData];
    const newIsSuperset = !updated[exerciseIndex].is_superset;
    updated[exerciseIndex] = {
      ...updated[exerciseIndex],
      is_superset: newIsSuperset,
      superset_group: newIsSuperset ? (updated[exerciseIndex].superset_group || 'A') : null
    };
    setWorkoutData(updated);
    workout.exercises = workout.exercises.map((ex, idx) =>
      idx === exerciseIndex ? { ...ex, is_superset: newIsSuperset, superset_group: newIsSuperset ? (ex.superset_group || 'A') : null } : ex
    );
    saveProgressNow(updated);
  }, [isCoachMode, workoutData, setWorkoutData, workout, saveProgressNow]);

  // Coach Mode: Update superset group on exercise
  const handleUpdateSupersetGroup = useCallback((exerciseIndex, group) => {
    if (!isCoachMode) return;
    const updated = [...workoutData];
    updated[exerciseIndex] = { ...updated[exerciseIndex], superset_group: group };
    setWorkoutData(updated);
    workout.exercises = workout.exercises.map((ex, idx) =>
      idx === exerciseIndex ? { ...ex, superset_group: group } : ex
    );
    saveProgressNow(updated);
  }, [isCoachMode, workoutData, setWorkoutData, workout, saveProgressNow]);

  // Autosave function - saves workout progress to backend
  // SAFARI FIX: Added mount guard and initialization check
  // OPTIMIZATION: Only saves when hasUnsavedChangesRef is true
  const performAutosave = useCallback(async (forceeSave = false) => {
    // Guard: Don't autosave if not initialized, unmounted, or already saving
    if (!isInitialized || !isMountedRef.current || !workout?.id || isSaving) {
      console.log('[AUTOSAVE] Skipped - not ready:', { isInitialized, isMounted: isMountedRef.current, workoutId: workout?.id, isSaving });
      return;
    }
    
    // Guard: Don't save if no changes (unless forced)
    if (!forceeSave && !hasUnsavedChangesRef.current) {
      console.log('[AUTOSAVE] Skipped - no changes to save');
      return;
    }
    
    // Guard: Don't save empty data
    if (!workoutData || workoutData.length === 0) {
      console.log('[AUTOSAVE] Skipped - no workout data');
      return;
    }
    
    try {
      setIsSaving(true);
      setAutosaveError(null);
      
      // Format exercises for backend (shared builder)
      const exercisesToSave = buildExercisesToSave(workoutData, exerciseNotes);
      
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      // Match saveProgressNow: when Coach Mode is active, attach the elevation
      // header so the trainer's structural changes survive the autosave round-trip.
      const headers = { ...getAuthHeaders() };
      if (isCoachMode) headers['X-Coach-Mode'] = 'true';
      await axios.put(`${API}/workouts/${workout.id}/save-progress`, exercisesToSave, { headers });
      
      // Only update state if still mounted
      if (isMountedRef.current) {
        setLastAutosave(new Date());
        setIsDirty(false); // Clear dirty state after successful save
        hasUnsavedChangesRef.current = false; // Clear change flag after successful save
        // Store reference of what was saved for change detection
        lastSavedDataRef.current = JSON.stringify(exercisesToSave);
        markSynced(workout.id);
        setSyncState('synced');
        console.log('[AUTOSAVE] Progress saved successfully at', new Date().toLocaleTimeString());
      }
    } catch (error) {
      console.error('[AUTOSAVE] Error:', error);
      if (isMountedRef.current) {
        setAutosaveError('Error al guardar');
        setSyncState('pending');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [workout?.id, workoutData, exerciseNotes, isSaving, isInitialized, isCoachMode]);
  
  // Debounced autosave trigger - marks data as changed and schedules save
  const triggerAutosave = useCallback(() => {
    // Guard: Only trigger if initialized
    if (!isInitialized) {
      console.log('[AUTOSAVE] Trigger skipped - not initialized');
      return;
    }
    
    // Mark that we have unsaved changes
    hasUnsavedChangesRef.current = true;
    
    // Clear any pending autosave
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    // Schedule new autosave after delay
    autosaveTimeoutRef.current = setTimeout(() => {
      performAutosave();
    }, AUTOSAVE_DELAY);
  }, [performAutosave, isInitialized]);
  
  // Trigger autosave when workout data changes (sets added/edited)
  // SAFARI FIX: Added initialization check
  useEffect(() => {
    if (!isInitialized) return; // Wait for initialization
    
    const hasData = workoutData.some(ex => ex.actual_sets && ex.actual_sets.length > 0);
    if (hasData && isDirty) {
      triggerAutosave();
    }
  }, [workoutData, isDirty, triggerAutosave, isInitialized]);

  // =============================================
  // P0.1 OFFLINE DRAFT BUFFER
  // Write-through: persist the latest execution snapshot to localStorage on
  // every change so logged sets survive WiFi drops / Safari backgrounding,
  // independent of the (potentially-failing) network autosave.
  // =============================================
  useEffect(() => {
    if (!isInitialized || !workout?.id) return;
    // Let recovery-on-mount evaluate the persisted draft BEFORE the first
    // write-through clobbers it with freshly-hydrated server state.
    if (!recoveryDoneRef.current) return;
    const hasData = workoutData.some(ex => ex.actual_sets && ex.actual_sets.length > 0);
    if (!hasData) return;
    saveDraft(workout.id, buildExercisesToSave(workoutData, exerciseNotes));
    setSyncState('pending');
  }, [workoutData, exerciseNotes, isInitialized, workout?.id]);

  // Flush pending changes to the backend on reconnect / tab-hide / pagehide.
  useEffect(() => {
    if (!isInitialized) return;
    const flush = () => {
      if (hasUnsavedChangesRef.current && isMountedRef.current) {
        performAutosave(true);
      }
    };
    const onOnline = () => flush();
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    const onPageHide = () => flush();
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [isInitialized, performAutosave]);

  // Recovery on mount: restore an unsynced draft only when it is strictly
  // newer than the last confirmed save AND holds more logged sets than the
  // server-hydrated state (prevents stale drafts from resurfacing).
  useEffect(() => {
    if (!isInitialized || recoveryDoneRef.current || !workout?.id) return;
    recoveryDoneRef.current = true;

    const draft = getDraft(workout.id);
    if (!isDraftRecoverable(draft)) return;

    const draftById = {};
    (draft.exercises || []).forEach((ex) => {
      if (ex.exercise_id) draftById[ex.exercise_id] = ex;
    });

    let recovered = false;
    const merged = workoutData.map((ex) => {
      const d = draftById[ex.exercise_id];
      const draftSets = d?.actual_sets?.length || 0;
      const currentSets = ex.actual_sets?.length || 0;
      if (d && draftSets > currentSets) {
        recovered = true;
        return { ...ex, actual_sets: d.actual_sets };
      }
      return ex;
    });

    if (recovered) {
      setWorkoutData(merged);
      hasUnsavedChangesRef.current = true;
      setIsDirty(true);
      setSyncState('pending');
      setShowRecoveryToast(true);
      setTimeout(() => { if (isMountedRef.current) setShowRecoveryToast(false); }, 4000);
      saveProgressNow(merged); // flush recovered progress to the backend
    }
  }, [isInitialized, workout?.id]);
  
  // Trigger autosave when exercise notes change
  // SAFARI FIX: Added initialization check
  useEffect(() => {
    if (!isInitialized) return; // Wait for initialization
    
    const hasNotes = Object.values(exerciseNotes).some(note => note && note.trim());
    if (hasNotes) {
      triggerAutosave();
    }
  }, [exerciseNotes, triggerAutosave, isInitialized]);
  
  // Periodic autosave every 30 seconds as backup (crash protection)
  // OPTIMIZATION: Only sends API request when hasUnsavedChangesRef is true
  useEffect(() => {
    if (!isInitialized) return; // Wait for initialization
    
    const periodicAutosave = setInterval(() => {
      // Only perform autosave if there are unsaved changes
      if (hasUnsavedChangesRef.current && isMountedRef.current) {
        console.log('[AUTOSAVE] Periodic check - saving unsaved changes');
        performAutosave();
      }
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(periodicAutosave);
  }, [performAutosave, isInitialized]);
  
  // Autosave when changing exercises (only trigger, don't add dependencies that would cause loops)
  // SAFARI FIX: Added initialization check
  const prevExerciseIndexRef = useRef(currentExerciseIndex);
  useEffect(() => {
    if (!isInitialized) return; // Wait for initialization
    
    if (prevExerciseIndexRef.current !== currentExerciseIndex) {
      prevExerciseIndexRef.current = currentExerciseIndex;
      const hasData = workoutData.some(ex => ex.actual_sets && ex.actual_sets.length > 0);
      if (hasData && hasUnsavedChangesRef.current) {
        performAutosave();
      }
    }
  }, [currentExerciseIndex, workoutData, performAutosave, isInitialized]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);
  
  // ERP Phase 4.7: Fetch Personal Records for workout exercises
  useEffect(() => {
    const fetchPRs = async () => {
      if (!workout?.id) return;
      
      try {
        const response = await axios.get(`${API}/workouts/${workout.id}/exercise-prs`, { headers: getAuthHeaders() });
        if (response.data?.prs) {
          setExercisePRs(response.data.prs);
          console.log('[PR] Loaded exercise PRs:', Object.keys(response.data.prs).length);
        }
      } catch (error) {
        console.error('[PR] Error fetching exercise PRs:', error);
        // Non-blocking - PRs are optional enhancement
      }
    };
    
    fetchPRs();
  }, [workout?.id]);

  // Fetch client's exercise history for weight autofill
  useEffect(() => {
    const fetchClientUsage = async () => {
      if (!workout?.client_id || workout.client_id === 'template') return;
      
      try {
        const response = await axios.get(`${API}/exercises/client-usage?client_id=${workout.client_id}`, { headers: getAuthHeaders() });
        if (response.data) {
          setClientExerciseUsage(response.data);
          console.log('[AUTOFILL] Loaded client exercise history:', Object.keys(response.data).length, 'exercises');
        }
      } catch (error) {
        console.error('[AUTOFILL] Error fetching client exercise usage:', error);
        // Non-blocking - autofill is enhancement
      }
    };
    
    fetchClientUsage();
  }, [workout?.client_id]);

  // ---------------------------------------------------------------------------
  // Remote coach sync: poll the structure revision so we can OFFER a refresh
  // (manual apply — never auto-move the client mid-set). Applying merges the
  // new structure/targets from the server while KEEPING everything the client
  // has already logged (actual_sets), matching by exercise_id in order.
  // ---------------------------------------------------------------------------
  const applyCoachUpdate = useCallback(async () => {
    if (!workout?.id) return;
    setApplyingCoachUpdate(true);
    try {
      const res = await axios.get(`${API}/workouts/${workout.id}`, { headers: getAuthHeaders() });
      const remote = res.data?.exercises || [];

      // Preserve locally-logged actual_sets: bucket current data by exercise_id
      // and hand them out FIFO as we walk the authoritative remote structure.
      const localById = {};
      (workoutData || []).forEach((ex) => {
        const id = ex?.exercise_id;
        if (!id) return;
        (localById[id] = localById[id] || []).push(ex);
      });

      const currentId = activeExerciseIdRef.current;
      const execChanged = execChangedRef.current;
      const merged = remote.map((rex) => {
        const id = rex.exercise_id;
        const localMatch = localById[id] && localById[id].length ? localById[id].shift() : null;
        let actualSets;
        if (execChanged) {
          // A coach corrected the client's actual values: adopt the server's
          // corrected sets, but keep any EXTRA sets the client logged beyond
          // what the server has (so we never lose in-progress logging).
          const serverSets = rex.actual_sets || [];
          const localExtra = (localMatch?.actual_sets || []).slice(serverSets.length);
          actualSets = [...serverSets, ...localExtra];
        } else {
          // Structure-only change: the client stays authoritative for execution.
          actualSets = localMatch?.actual_sets || rex.actual_sets || [];
        }
        return {
          ...rex,
          actual_sets: actualSets,
          note: localMatch?.note ?? rex.note ?? '',
        };
      });

      setWorkoutData(merged);
      workout.exercises = remote;

      // Keep the client on the exercise they were doing (follow identity, not slot).
      let nextIndex = merged.findIndex((ex) => ex.exercise_id === currentId);
      if (nextIndex < 0) nextIndex = Math.min(currentExerciseIndex, merged.length - 1);
      setCurrentExerciseIndex(Math.max(0, nextIndex));

      // If this was an actual-value correction, acknowledge so the server lifts
      // its guard and normal autosave resumes.
      if (execChanged) {
        try { await axios.post(`${API}/workouts/${workout.id}/coach/ack-exec`, {}, { headers: getAuthHeaders() }); }
        catch { /* non-fatal */ }
      }

      // Re-sync the revision baselines.
      try {
        const rev = await axios.get(`${API}/workouts/${workout.id}/structure-rev`, { headers: getAuthHeaders() });
        lastSeenRevRef.current = rev.data?.structure_rev ?? lastSeenRevRef.current;
        lastSeenExecRef.current = rev.data?.exec_rev ?? lastSeenExecRef.current;
      } catch { /* keep prior baseline */ }

      execChangedRef.current = false;
      setCoachUpdateAvailable(false);
      setCoachUpdateIsExec(false);
    } catch (err) {
      console.error('[COACH-SYNC] Failed to apply update:', err);
    } finally {
      if (isMountedRef.current) setApplyingCoachUpdate(false);
    }
  }, [workout, workoutData, setWorkoutData, currentExerciseIndex, setCurrentExerciseIndex]);

  useEffect(() => {
    if (!workout?.id || workout.is_finished) return;
    let cancelled = false;

    const checkRev = async () => {
      try {
        const res = await axios.get(`${API}/workouts/${workout.id}/structure-rev`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const rev = res.data?.structure_rev ?? 0;
        const execRev = res.data?.exec_rev ?? 0;
        if (lastSeenRevRef.current === null) {
          lastSeenRevRef.current = rev;       // establish baselines on first poll
          lastSeenExecRef.current = execRev;
          // HARDENING: a coach correction was left un-acknowledged (client closed
          // before tapping the banner). The client displays the corrected values
          // on load, so adopt-and-ack now to lift the server guard — otherwise
          // future logging on that exercise would be silently dropped.
          if (res.data?.pending_exec_correction) {
            execChangedRef.current = true;
            applyCoachUpdate();
          }
        } else {
          const structChanged = rev > lastSeenRevRef.current;
          const execChanged = execRev > (lastSeenExecRef.current ?? 0);
          if (structChanged || execChanged) {
            if (execChanged) execChangedRef.current = true;
            setCoachUpdateIsExec(execChanged);
            setCoachUpdateAvailable(true);
          }
        }
      } catch { /* transient — try again next tick */ }
    };

    checkRev();
    const interval = setInterval(checkRev, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [workout?.id, workout?.is_finished]);

  // CRITICAL FIX: Sync currentSet from actual data when exercise changes
  // This ensures "Serie X/Y" always reflects the viewed exercise, not the previous one
  // Covers: sidebar clicks, prev/next buttons, superset navigation, pending navigation
  useEffect(() => {
    const completedSets = workoutData[currentExerciseIndex]?.actual_sets?.length || 0;
    setCurrentSet(completedSets + 1);
  }, [currentExerciseIndex]);

  // CRITICAL FIX: Initialize form data when exercise changes
  // Ensures weight is set from client history, not carried over from previous exercise
  const prevExerciseIdRef = useRef(null);
  useEffect(() => {
    if (!currentExercise?.exercise_id) return;
    
    // PART 2 FIX: Always keep activeExerciseIdRef in sync
    activeExerciseIdRef.current = currentExercise.exercise_id;
    
    // Only reset if we're actually changing to a different exercise
    if (prevExerciseIdRef.current === currentExercise.exercise_id) return;
    prevExerciseIdRef.current = currentExercise.exercise_id;
    
    // Resolve the NEXT set's plan (the one the user is about to perform).
    // If the exercise already has logged sets — e.g. the client closed the
    // app mid-workout and is resuming — the actual last logged weight wins
    // over the template. Otherwise we fall back to per-set plan[0], scalar
    // target_weight, or the client's most recent historical weight.
    const existingSets = workoutData[currentExerciseIndex]?.actual_sets || [];
    const lastLogged = existingSets.length > 0 ? existingSets[existingSets.length - 1] : null;
    const nextSetIdx = existingSets.length;
    const nextPlan = resolveSetTargets(currentExercise, nextSetIdx);
    const exerciseUsage = clientExerciseUsage[currentExercise.exercise_id];
    const defaultWeight =
      (lastLogged?.weight != null && lastLogged.weight !== '')
        ? lastLogged.weight
        : (nextPlan.weight ?? exerciseUsage?.last_weight ?? 0);
    const defaultRestTime = nextPlan.rest_time || 120;

    console.log(`[EXERCISE_CHANGE] Switched to "${currentExercise.exercise_name}":`, {
      exercise_id: currentExercise.exercise_id,
      index: currentExerciseIndex,
      historyWeight: exerciseUsage?.last_weight,
      perSetPlan: !!currentExercise.target_set_plan,
      usingWeight: defaultWeight,
      sourcedFrom: lastLogged ? 'last-actual-set' : (nextPlan.weight != null ? 'plan' : 'client-history')
    });

    // Reset form with correct values for this exercise
    // REPS: always empty by default — user must enter actual performance
    //       (target_reps is shown as placeholder in the input)
    // WEIGHT: first set uses target_weight (or client history); subsequent
    //         sets inherit the previous ACTUAL set's weight (above, and via
    //         the post-completion reset below).
    // RPE: always empty on exercise change — captured during rest.
    setSetData({
      reps: '',
      weight: defaultWeight.toString(),
      rest_time: defaultRestTime.toString(),
      rpe: ''
    });
    
  }, [currentExercise?.exercise_id, currentExerciseIndex, clientExerciseUsage, currentExercise?.target_weight, currentExercise?.weight, currentExercise?.rest_time, currentExercise?.exercise_name]);
  
  // Format last autosave time
  const formatAutosaveTime = (date) => {
    if (!date) return null;
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Muscle name translations for Spanish
  const muscleTranslations = {
    // Basic muscle groups
    'chest': 'pecho',
    'back': 'espalda',
    'shoulders': 'hombros',
    'biceps': 'bíceps',
    'triceps': 'tríceps',
    'forearms': 'antebrazos',
    'core': 'core',
    'abs': 'abdominales',
    'quadriceps': 'cuádriceps',
    'hamstrings': 'isquiotibiales',
    'glutes': 'glúteos',
    'calves': 'pantorrillas',
    'legs': 'piernas',
    'arms': 'brazos',
    // Detailed muscles
    'pectoralis major': 'pectoral mayor',
    'pectoralis major (sternal)': 'pectoral mayor (esternal)',
    'pectoralis major (clavicular)': 'pectoral mayor (clavicular)',
    'pectoralis minor': 'pectoral menor',
    'anterior deltoid': 'deltoides anterior',
    'lateral deltoid': 'deltoides lateral',
    'posterior deltoid': 'deltoides posterior',
    'triceps brachii': 'tríceps braquial',
    'biceps brachii': 'bíceps braquial',
    'biceps brachii (short head)': 'bíceps braquial (cabeza corta)',
    'biceps brachii (long head)': 'bíceps braquial (cabeza larga)',
    'latissimus dorsi': 'dorsal ancho',
    'rhomboids': 'romboides',
    'trapezius': 'trapecio',
    'trapezius (middle)': 'trapecio (medio)',
    'trapezius (lower)': 'trapecio (inferior)',
    'erector spinae': 'erectores espinales',
    'rotator cuff': 'manguito rotador',
    'serratus anterior': 'serrato anterior',
    'rectus abdominis': 'recto abdominal',
    'obliques': 'oblicuos',
    'transverse abdominis': 'transverso abdominal',
    'gluteus maximus': 'glúteo mayor',
    'gluteus medius': 'glúteo medio',
    'quadriceps femoris': 'cuádriceps femoral',
    'vastus lateralis': 'vasto lateral',
    'vastus medialis': 'vasto medial',
    'rectus femoris': 'recto femoral',
    'biceps femoris': 'bíceps femoral',
    'semitendinosus': 'semitendinoso',
    'semimembranosus': 'semimembranoso',
    'gastrocnemius': 'gastrocnemio',
    'soleus': 'sóleo',
    'hip flexors': 'flexores de cadera',
    'hip adductors': 'aductores de cadera',
    'brachialis': 'braquial',
    'brachioradialis': 'braquiorradial',
    'wrist flexors': 'flexores de muñeca',
    'wrist extensors': 'extensores de muñeca'
  };

  // Translate a muscle name
  const translateMuscle = (muscle) => {
    if (language !== 'es' || !muscle) return muscle;
    const lowerMuscle = muscle.toLowerCase();
    return muscleTranslations[lowerMuscle] || muscle;
  };

  // Translate an array of muscle names
  const translateMuscles = (muscles) => {
    if (!muscles || !Array.isArray(muscles)) return muscles;
    return muscles.map(m => translateMuscle(m));
  };

  // PART 5 FIX: Preload images for upcoming exercises to prevent delay
  useEffect(() => {
    const preloadExerciseImages = async () => {
      // Preload current and next 2 exercises
      const indicesToPreload = [
        currentExerciseIndex,
        currentExerciseIndex + 1,
        currentExerciseIndex + 2
      ].filter(i => i < workout.exercises.length);
      
      for (const idx of indicesToPreload) {
        const exercise = workout.exercises[idx];
        const exerciseId = exercise.exercise_id;
        
        // Skip if already cached
        if (imageCache[exerciseId]) continue;
        
        try {
          // Try to fetch exercise data
          let imageUrl = exercise.image_url;
          
          if (!imageUrl && exerciseId) {
            const response = await axios.get(`${API}/exercises/${exerciseId}`);
            imageUrl = response.data?.image_url;
          }
          
          if (imageUrl) {
            // Preload the image
            const img = new Image();
            img.src = imageUrl;
            
            // Cache it
            setImageCache(prev => ({
              ...prev,
              [exerciseId]: imageUrl
            }));
          }
        } catch (err) {
          // Silently fail preloading
        }
      }
    };
    
    preloadExerciseImages();
  }, [currentExerciseIndex, workout.exercises]);

  // PART 5 FIX: Fetch full exercise data with immediate image display from cache
  // PERFORMANCE FIX: Use pre-stored Spanish fields directly - NO AI translation needed
  useEffect(() => {
    const fetchExerciseData = async () => {
      // PART 5: Immediately set cached image if available to prevent delay
      const cachedImage = imageCache[currentExercise.exercise_id];
      if (cachedImage && !fullExerciseData?.image_url) {
        setFullExerciseData(prev => ({
          ...prev,
          ...currentExercise,
          image_url: cachedImage
        }));
      }
      
      setIsLoadingImage(true);
      
      try {
        // Fetch full exercise data (for the details/technique modal) by id only.
        // The displayed image does NOT depend on this — it renders immediately from
        // currentExercise.image_url — so a slow/missing fetch never delays navigation.
        let exerciseData = null;
        try {
          const response = await axios.get(`${API}/exercises/${currentExercise.exercise_id}`);
          exerciseData = response.data;
        } catch (error) {
          // Not found by id → fall back to the data already on the workout exercise.
          // (Previously this fetched the ENTIRE exercise library to match by name,
          //  which blocked the UI for several seconds on every navigation.)
          exerciseData = null;
        }
        
        // If we found exercise data, use it. Otherwise, use data from currentExercise
        if (exerciseData) {
          // Cache the image URL
          if (exerciseData.image_url) {
            setImageCache(prev => ({
              ...prev,
              [currentExercise.exercise_id]: exerciseData.image_url
            }));
          }
          
          // PERFORMANCE FIX: Use pre-stored Spanish fields directly from the database
          // All exercises already have name_es, execution_steps_es, form_tips_es, etc.
          // NO AI translation call needed - just map the _es fields
          if (language === 'es') {
            exerciseData = {
              ...exerciseData,
              // Use Spanish versions of all translated fields
              name: exerciseData.name_es || exerciseData.name,
              execution_steps: exerciseData.execution_steps_es || exerciseData.execution_steps,
              form_tips: exerciseData.form_tips_es || exerciseData.form_tips,
              common_mistakes: exerciseData.common_mistakes_es || exerciseData.common_mistakes,
              benefits: exerciseData.benefits_es || exerciseData.benefits,
              target_muscles: exerciseData.target_muscles_es || exerciseData.target_muscles,
            };
          }
          
          // Set exercise data immediately - all Spanish content is already included
          setFullExerciseData(exerciseData);
        } else {
          // Fallback: use image_url from currentExercise or cache if available
          setFullExerciseData({
            ...currentExercise,
            image_url: currentExercise.image_url || cachedImage || null
          });
        }
      } catch (error) {
        console.error('Error fetching exercise data:', error);
        // Fallback: use currentExercise data with cached image
        const cachedImage = imageCache[currentExercise.exercise_id];
        setFullExerciseData({
          ...currentExercise,
          image_url: currentExercise.image_url || cachedImage || null
        });
      } finally {
        setIsLoadingImage(false);
      }
    };
    
    if (currentExercise.exercise_id || currentExercise.exercise_name) {
      fetchExerciseData();
    }
  }, [currentExercise.exercise_id, currentExercise.exercise_name, currentExercise.image_url, language]);
  
  // Get the best available image URL.
  // Prefer the per-exercise enriched media (immediate, correct on navigation) so the image
  // updates instantly when switching exercises — never wait on the details fetch, and never
  // fall back to fullExerciseData first (which is the PREVIOUS exercise until the fetch resolves).
  const displayImageUrl = currentExercise.image_url || currentExercise.animation_webp || imageCache[currentExercise.exercise_id] || fullExerciseData?.image_url;

  // =============================================
  // AUDIO SYSTEM: Reliable bell sound for tablets
  // =============================================
  const audioContextRef = useRef(null);
  const bellBufferRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  // Distinct rest-timer sound (0-4) assigned by the backend per active session
  // so up to 5 simultaneous trainings ring differently.
  const [soundIndex, setSoundIndex] = useState(0);

  // Initialize AudioContext (the specific sound buffer is built once the slot is known)
  useEffect(() => {
    const initAudio = async () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
          console.warn('[AUDIO] Web Audio API not supported');
          return;
        }
        audioContextRef.current = new AudioCtx();
        setAudioReady(true);
        console.log('[AUDIO] AudioContext ready');
      } catch (err) {
        console.error('[AUDIO] Failed to initialize:', err);
      }
    };
    initAudio();
    
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // (Re)build the alert buffer whenever the assigned sound changes
  useEffect(() => {
    const ctx = audioContextRef.current;
    if (!audioReady || !ctx) return;
    try {
      bellBufferRef.current = synthesizeTimerSound(ctx, soundIndex);
      console.log('[AUDIO] Timer sound ready (index', soundIndex, ')');
    } catch (err) {
      console.error('[AUDIO] Failed to synthesize timer sound:', err);
    }
  }, [audioReady, soundIndex]);

  // Claim a distinct sound slot for this session + heartbeat + release
  useEffect(() => {
    const workoutId = workout?.id;
    if (!workoutId) return;
    let cancelled = false;

    const claim = async () => {
      try {
        const res = await axios.post(`${API}/workouts/sound-slot/claim`, { workout_id: workoutId }, { headers: getAuthHeaders() });
        if (!cancelled && typeof res.data?.sound_index === 'number') {
          setSoundIndex(res.data.sound_index);
        }
      } catch (e) {
        console.warn('[AUDIO] Sound slot claim failed, using default sound:', e?.message);
      }
    };
    claim();
    const heartbeat = setInterval(() => {
      axios.post(`${API}/workouts/sound-slot/heartbeat`, { workout_id: workoutId }, { headers: getAuthHeaders() }).catch(() => {});
    }, 25000);

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      axios.post(`${API}/workouts/sound-slot/release`, { workout_id: workoutId }, { headers: getAuthHeaders() }).catch(() => {});
    };
  }, [workout?.id]);
  
  // Unlock AudioContext on first user interaction (required by tablet browsers)
  useEffect(() => {
    const unlockAudio = async () => {
      if (audioUnlockedRef.current) return;
      const ctx = audioContextRef.current;
      if (!ctx) return;
      
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
          console.log('[AUDIO] AudioContext resumed after user interaction');
        } catch (e) {
          console.warn('[AUDIO] Failed to resume AudioContext:', e);
        }
      }
      audioUnlockedRef.current = true;
      setAudioEnabled(true);
    };
    
    document.addEventListener('touchstart', unlockAudio, { once: true });
    document.addEventListener('click', unlockAudio, { once: true });
    
    return () => {
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('click', unlockAudio);
    };
  }, []);
  
  // Reliable bell sound playback
  const playBellSound = useCallback(() => {
    const ctx = audioContextRef.current;
    const buffer = bellBufferRef.current;
    
    // Method 1: Web Audio API (most reliable on tablets)
    if (ctx && buffer) {
      // Resume if suspended (can happen after long inactivity)
      const play = () => {
        try {
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          const gainNode = ctx.createGain();
          gainNode.gain.value = 0.7;
          source.connect(gainNode);
          gainNode.connect(ctx.destination);
          source.start(0);
          console.log('[AUDIO] Bell played via Web Audio API');
        } catch (err) {
          console.error('[AUDIO] Web Audio playback failed:', err);
          fallbackAlert();
        }
      };
      
      if (ctx.state === 'suspended') {
        ctx.resume().then(play).catch(() => fallbackAlert());
      } else {
        play();
      }
    } else {
      fallbackAlert();
    }
    
    // Vibration fallback (works on most tablets/phones)
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 300]);
    }
  }, []);
  
  // Visual/vibration fallback when audio fails
  const fallbackAlert = useCallback(() => {
    console.log('[AUDIO] Using visual fallback');
    if (navigator.vibrate) {
      navigator.vibrate([300, 200, 300, 200, 500]);
    }
  }, []);
  
  // Test sound function (exposed to UI)
  const handleTestSound = useCallback(() => {
    const ctx = audioContextRef.current;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().then(() => {
        playBellSound();
        setAudioEnabled(true);
      });
    } else {
      playBellSound();
      setAudioEnabled(true);
    }
  }, [playBellSound]);

  // Live elapsed timer - updates every second
  useEffect(() => {
    // Persist the start anchor once so a later remount (app kill / reload)
    // continues counting from the real start instead of resetting to 0.
    if (workout?.id && workoutStartTime) {
      saveStartAnchor(workout.id, workoutStartTime.toISOString());
    }
    const timer = setInterval(() => {
      const elapsed = Math.floor((new Date() - workoutStartTime) / 1000);
      setElapsedTime(Math.min(elapsed, MAX_SESSION_SECONDS));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [workoutStartTime, workout?.id]);

  // =============================================
  // CARDIO MODE: Detection + Timer Logic
  // =============================================
  const isCardioExercise = fullExerciseData?.exercise_type === 'cardio';

  // SUPERSET SEQUENTIAL GATE (clients only): inside a superset group, an exercise
  // that is NOT the first in its group cannot log set N until the previous
  // exercise in the same group has already logged set N. Coach Mode is exempt.
  const supersetGate = (() => {
    if (isCoachMode) return { blocked: false };
    const cur = workout.exercises[currentExerciseIndex];
    const prev = workout.exercises[currentExerciseIndex - 1];
    if (!cur?.is_superset) return { blocked: false };
    const group = cur.superset_group || 'A';
    // First exercise in the group has no preceding same-group member → never blocked
    if (!prev?.is_superset || (prev.superset_group || 'A') !== group) return { blocked: false };
    const curSets = workoutData[currentExerciseIndex]?.actual_sets?.length || 0;
    const prevSets = workoutData[currentExerciseIndex - 1]?.actual_sets?.length || 0;
    if (prevSets > curSets) return { blocked: false };
    return {
      blocked: true,
      prevName: prev.exercise_name_es || prev.exercise_name || 'el ejercicio anterior',
      requiredSet: curSets + 1,
    };
  })();

  // Reset cardio state when switching exercises
  const prevCardioExIdRef = useRef(null);
  useEffect(() => {
    if (!currentExercise?.exercise_id) return;
    if (prevCardioExIdRef.current === currentExercise.exercise_id) return;
    prevCardioExIdRef.current = currentExercise.exercise_id;
    
    // Reset cardio timer state for new exercise
    setCardioTimer(0);
    setIsCardioTimerRunning(false);
    setCardioTargetTime(60);
    setCardioRounds([]);
    setCardioTargetHit(false);
    cardioStartRef.current = null;
    if (cardioTimerRef.current) {
      clearInterval(cardioTimerRef.current);
      cardioTimerRef.current = null;
    }
    if (cardioPreCountdownRef.current) {
      clearInterval(cardioPreCountdownRef.current);
      cardioPreCountdownRef.current = null;
    }
    setCardioPreCountdown(null);
  }, [currentExercise?.exercise_id]);
  
  // Cardio timer interval — WALL-CLOCK based (survives screen-lock / backgrounding)
  useEffect(() => {
    if (!isCardioTimerRunning) {
      cardioStartRef.current = null; // drop anchor so resume continues from paused value
      if (cardioTimerRef.current) {
        clearInterval(cardioTimerRef.current);
        cardioTimerRef.current = null;
      }
      return;
    }
    // Anchor so the current displayed value maps to now (handles resume-after-pause).
    if (!cardioStartRef.current) cardioStartRef.current = Date.now() - (cardioTimer * 1000);
    const tick = () => {
      setCardioTimer(Math.max(0, Math.round((Date.now() - cardioStartRef.current) / 1000)));
    };
    cardioTimerRef.current = setInterval(tick, 1000);
    const onVisibility = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    tick();
    return () => {
      if (cardioTimerRef.current) {
        clearInterval(cardioTimerRef.current);
        cardioTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isCardioTimerRunning]);
  
  // Cardio: when a TIMED set reaches its target, ring the bell, STOP the clock
  // ("countdown" model) and prompt the athlete to confirm the round. Manual
  // sets (target 0) keep counting up as an open stopwatch and never auto-stop.
  const cardioTargetReachedRef = useRef(false);
  useEffect(() => {
    if (isCardioTimerRunning && cardioTargetTime > 0 && cardioTimer >= cardioTargetTime && !cardioTargetReachedRef.current) {
      cardioTargetReachedRef.current = true;
      playBellSound();
      setIsCardioTimerRunning(false);   // auto-stop the set at the planned target
      setCardioTargetHit(true);
      try { if (navigator.vibrate) navigator.vibrate([180, 90, 180]); } catch (e) {}
      broadcastCardioLive(false);       // best-effort; not in deps (defined below)
      console.log(`[CARDIO] Target reached (${cardioTargetTime}s) — auto-stopped`);
    }
  }, [cardioTimer, cardioTargetTime, isCardioTimerRunning, playBellSound]);
  
  // Reset the target flags when the timer resets or the target grows past elapsed
  useEffect(() => {
    if (cardioTimer === 0 || cardioTimer < cardioTargetTime) {
      cardioTargetReachedRef.current = false;
      setCardioTargetHit(false);
    }
  }, [cardioTimer, cardioTargetTime]);
  
  // Cardio: broadcast live running state to the Live Board (event-driven, best-effort)
  const broadcastCardioLive = useCallback((running) => {
    axios.put(`${API}/workouts/${workout.id}/cardio-live`, {
      running,
      started_at: running ? new Date().toISOString() : null,
      base_seconds: cardioTimer,
      target_seconds: cardioTargetTime,
      exercise_index: currentExerciseIndex,
    }).catch(() => { /* board falls back to rounds if this fails */ });
  }, [workout.id, cardioTimer, cardioTargetTime, currentExerciseIndex]);

  // Cardio: 5-second courtesy pre-countdown (grab the rope, get into position…)
  const [cardioPreCountdown, setCardioPreCountdown] = useState(null);
  const cardioPreCountdownRef = useRef(null);

  const clearCardioCountdown = useCallback(() => {
    if (cardioPreCountdownRef.current) {
      clearInterval(cardioPreCountdownRef.current);
      cardioPreCountdownRef.current = null;
    }
    setCardioPreCountdown(null);
  }, []);

  const beginCardioCountdown = useCallback(() => {
    if (cardioPreCountdownRef.current) return;
    try { playBellSound(); } catch (e) {}
    setCardioPreCountdown(5);
    cardioPreCountdownRef.current = setInterval(() => {
      setCardioPreCountdown(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(cardioPreCountdownRef.current);
          cardioPreCountdownRef.current = null;
          setIsCardioTimerRunning(true);
          broadcastCardioLive(true);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [broadcastCardioLive, playBellSound]);

  // Cardio: Start (with 5s courtesy) / Pause timer
  const handleCardioToggleTimer = useCallback(() => {
    if (isCardioTimerRunning) {
      setIsCardioTimerRunning(false);
      broadcastCardioLive(false);
    } else if (cardioPreCountdown !== null) {
      clearCardioCountdown();               // tapping during countdown cancels it
    } else {
      beginCardioCountdown();
    }
  }, [isCardioTimerRunning, cardioPreCountdown, beginCardioCountdown, clearCardioCountdown, broadcastCardioLive]);

  // Cardio: Reset timer
  const handleCardioResetTimer = useCallback(() => {
    clearCardioCountdown();
    setIsCardioTimerRunning(false);
    setCardioTimer(0);
    setCardioTargetHit(false);
    broadcastCardioLive(false);
  }, [clearCardioCountdown, broadcastCardioLive]);

  // "+ info" — fetch and show the finished-workout summary for a past session
  // (opened from the PR / last-session rows in the exercise header).
  const handleOpenDayWorkout = useCallback(async (workoutId) => {
    if (!workoutId || dayWorkoutLoading) return;
    setDayWorkoutLoading(true);
    try {
      const res = await axios.get(`${API}/workouts/${workoutId}`, { headers: getAuthHeaders() });
      setDayWorkout(res.data);
    } catch (e) {
      console.error('[INFO POPUP] Failed to load workout', workoutId, e);
    } finally {
      setDayWorkoutLoading(false);
    }
  }, [dayWorkoutLoading]);
  
  // Cardio: Complete a round (save elapsed time as a "set")
  const handleCardioCompleteRound = useCallback(() => {
    if (cardioTimer === 0) return;
    
    // Stop timer
    setIsCardioTimerRunning(false);
    broadcastCardioLive(false);
    
    const roundTime = cardioTimer;
    
    // Store as actual_set in the standard format for compatibility
    const newSet = {
      reps: 1, // 1 round
      weight: 0,
      time: roundTime, // cardio-specific: elapsed seconds
      rest_time: parseInt(setData.rest_time) || 60,
      rpe: null,
      completed_at: new Date().toISOString()
    };
    
    // Save to workoutData (same mechanism as strength sets)
    const updatedWorkoutData = [...workoutData];
    if (!updatedWorkoutData[currentExerciseIndex].actual_sets) {
      updatedWorkoutData[currentExerciseIndex].actual_sets = [];
    }
    
    const completedSetIndex = updatedWorkoutData[currentExerciseIndex].actual_sets.length;
    updatedWorkoutData[currentExerciseIndex].actual_sets.push(newSet);
    setWorkoutData(updatedWorkoutData);
    
    // Immediate save for Live Board
    saveProgressNow(updatedWorkoutData);
    
    // Track for RPE
    lastCompletedSetRef.current = { exerciseIndex: currentExerciseIndex, setIndex: completedSetIndex };
    setLastCompletedSetRpe(null);
    
    // Update cardio rounds display
    setCardioRounds(prev => [...prev, { time: roundTime, completed_at: new Date().toISOString() }]);
    
    // Reset timer for next round
    setCardioTimer(0);
    setCardioTargetHit(false);
    
    const targetSets = currentExercise.target_sets || currentExercise.sets || 4;
    const completedCount = updatedWorkoutData[currentExerciseIndex].actual_sets.length;
    
    console.log(`[CARDIO] Round ${completedCount} completed: ${roundTime}s, target rounds: ${targetSets}`);
    
    if (completedCount >= targetSets) {
      // All rounds complete — move to next exercise
      console.log('[CARDIO] All rounds complete!');
      updatedWorkoutData[currentExerciseIndex].completed = true;
      setWorkoutData(updatedWorkoutData);
      
      if (currentExerciseIndex < workout.exercises.length - 1) {
        const restTime = parseInt(setData.rest_time) || 60;
        // Start NEW rest: elapsed=0, target=restTime (RPE may refine target later)
        setRestTimer(0);
        setRestTarget(restTime);
        restJustEndedRef.current = false;
        setIsResting(true);
        setCurrentRestStartTime(new Date());
        currentRestStartTimeRef.current = new Date();
        setPendingNavigation({ type: 'next_exercise', nextIndex: currentExerciseIndex + 1 });
      } else {
        // Last exercise — trigger finish flow
        const totalDuration = Math.min(Math.floor((new Date() - workoutStartTime) / 1000), MAX_SESSION_SECONDS);
        const clampedRestTime = Math.min(totalRestTime, totalDuration);
        const effectiveTrainingTime = Math.max(0, totalDuration - clampedRestTime);
        setPendingFinishData({ totalDuration, totalRestTime: clampedRestTime, effectiveTrainingTime });
        
        const lastSetIndex = updatedWorkoutData[currentExerciseIndex].actual_sets.length - 1;
        setPendingLastSetRpe({
          exerciseIndex: currentExerciseIndex,
          setIndex: lastSetIndex,
          exerciseName: currentExercise.exercise_name_es || currentExercise.exercise_name
        });
        setShowLastSetRpeModal(true);
      }
    } else {
      // More rounds to go — start rest timer
      const restTime = parseInt(setData.rest_time) || 60;
      setRestTimer(0);
      setRestTarget(restTime);
      restJustEndedRef.current = false;
      setIsResting(true);
      setCurrentRestStartTime(new Date());
      currentRestStartTimeRef.current = new Date();
    }
  }, [cardioTimer, workoutData, currentExerciseIndex, setWorkoutData, saveProgressNow, currentExercise, workout, setData.rest_time, workoutStartTime, totalRestTime, broadcastCardioLive]);
  
  // Cardio: Format timer display
  const formatCardioTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Rest timer — ELAPSED stopwatch (counts up). Never reset by RPE.
  // Bell + auto-end fire DIRECTLY inside the interval callback (the most
  // reliable pattern; mirrors the original countdown architecture). The
  // dedicated effect below handles the "RPE lowered target below elapsed"
  // edge case where the threshold check happens between ticks.
  const restJustEndedRef = useRef(false);
  const restStartRef = useRef(null); // wall-clock anchor (ms) for the rest display timer
  const restTargetRef = useRef(restTarget);
  useEffect(() => { restTargetRef.current = restTarget; }, [restTarget]);

  const finishRest = useCallback(() => {
    if (restJustEndedRef.current) return;
    restJustEndedRef.current = true;
    console.log(`[REST] Target reached — playing bell + ending rest`);
    try {
      playBellSound();
    } catch (e) {
      console.error('[REST] Bell sound failed:', e);
    }
    setIsResting(false);
    if (currentRestStartTimeRef.current) {
      const actualRestTime = Math.floor((new Date() - currentRestStartTimeRef.current) / 1000);
      setTotalRestTime((prevTotal) => prevTotal + actualRestTime);
      currentRestStartTimeRef.current = null;
      setCurrentRestStartTime(null);
    }
  }, [playBellSound]);

  useEffect(() => {
    // WALL-CLOCK rest timer: compute elapsed from a Date.now() anchor instead of
    // counting ticks. setInterval is throttled/frozen when the phone screen locks
    // or the PWA is backgrounded, which made tick-counted timers drift. Anchoring
    // to wall-clock + resyncing on visibilitychange keeps the timer accurate and
    // fires the bell/auto-end immediately on resume if the target passed while away.
    if (!isResting) {
      restStartRef.current = null;
      return;
    }
    if (!restStartRef.current) restStartRef.current = Date.now();
    const tick = () => {
      const elapsed = Math.max(0, Math.round((Date.now() - restStartRef.current) / 1000));
      setRestTimer(elapsed);
      const target = restTargetRef.current;
      if (target > 0 && elapsed >= target && !restJustEndedRef.current) {
        Promise.resolve().then(finishRest);
      }
    };
    const interval = setInterval(tick, 1000);
    const onVisibility = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    tick(); // immediate sync on (re)start / resume
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isResting, finishRest]);

  // Edge case: RPE LOWERED restTarget below the current elapsed value while
  // the interval hasn't ticked yet. Trigger end immediately in that case.
  useEffect(() => {
    if (
      isResting &&
      restTarget > 0 &&
      restTimer >= restTarget &&
      !restJustEndedRef.current
    ) {
      finishRest();
    }
  }, [isResting, restTimer, restTarget, finishRest]);

  // AudioContext keep-alive during rest (prevents tablet browsers from suspending it)
  useEffect(() => {
    if (!isResting) return;
    const ctx = audioContextRef.current;
    if (!ctx) return;
    
    const keepAlive = setInterval(() => {
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
    }, 3000);
    
    return () => clearInterval(keepAlive);
  }, [isResting]);
  
  // Handle pending navigation after rest completes
  useEffect(() => {
    if (!isResting && pendingNavigation) {
      const { type, nextIndex } = pendingNavigation;
      if (type === 'next_exercise') {
        // Use a microtask to batch state updates
        Promise.resolve().then(() => {
          setCurrentExerciseIndex(nextIndex);
          // currentSet synced by useEffect on currentExerciseIndex change
        });
        console.log('➡️ Moving to next exercise after rest');
      }
      setPendingNavigation(null);
    }
  }, [isResting, pendingNavigation, setCurrentExerciseIndex]);

  // SAFARI FIX: Ref to prevent double-tap triggering multiple set completions
  const isCompletingSetRef = useRef(false);

  const handleCompleteSet = useCallback(() => {
    // Guard against double-tap on Safari/iOS
    if (isCompletingSetRef.current) {
      console.log('[SAFARI] Blocked duplicate handleCompleteSet call');
      return;
    }
    isCompletingSetRef.current = true;
    
    // Release lock after animation frame (or timeout for safety)
    requestAnimationFrame(() => {
      setTimeout(() => {
        isCompletingSetRef.current = false;
      }, 300);
    });

    // SUPERSET SEQUENTIAL GATE (defense-in-depth; clients only): block logging
    // set N of a non-first superset-group exercise until the previous group member
    // has logged set N. Mirrors the disabled-button UI guard.
    if (!isCoachMode) {
      const curStruct = workout.exercises[currentExerciseIndex];
      const prevStruct = workout.exercises[currentExerciseIndex - 1];
      if (
        curStruct?.is_superset &&
        prevStruct?.is_superset &&
        (prevStruct.superset_group || 'A') === (curStruct.superset_group || 'A')
      ) {
        const curSetsLen = workoutData[currentExerciseIndex]?.actual_sets?.length || 0;
        const prevSetsLen = workoutData[currentExerciseIndex - 1]?.actual_sets?.length || 0;
        if (prevSetsLen <= curSetsLen) {
          console.log('[SUPERSET_GATE] Blocked out-of-order superset set logging');
          return;
        }
      }
    }
    
    // PART 2 FIX: Validate exercise_id before saving to prevent data corruption
    const targetExercise = workoutData[currentExerciseIndex];
    const expectedExerciseId = activeExerciseIdRef.current;
    
    if (!targetExercise || targetExercise.exercise_id !== expectedExerciseId) {
      console.error('[SET_SAVE] BLOCKED: exercise_id mismatch!', {
        activeExerciseId: expectedExerciseId,
        targetExerciseId: targetExercise?.exercise_id,
        currentExerciseIndex
      });
      return;
    }
    
    const newSet = {
      reps: parseInt(setData.reps) || 0,
      weight: parseFloat(String(setData.weight).replace(',', '.')) || 0,
      rest_time: parseInt(setData.rest_time) || 120,
      rpe: parseInt(setData.rpe) || null,
      completed_at: new Date().toISOString()
    };

    // Update workout data (verified exercise_id match above)
    const updatedWorkoutData = [...workoutData];
    if (!updatedWorkoutData[currentExerciseIndex].actual_sets) {
      updatedWorkoutData[currentExerciseIndex].actual_sets = [];
    }
    
    // Track the set index BEFORE pushing so we know where to retroactively save RPE
    const completedSetIndex = updatedWorkoutData[currentExerciseIndex].actual_sets.length;
    updatedWorkoutData[currentExerciseIndex].actual_sets.push(newSet);
    setWorkoutData(updatedWorkoutData);
    
    // LIVE BOARD FIX: Immediately save to backend so Live Board sees the update
    // This bypasses the 3s debounce that was causing the board to lag 1 set behind
    saveProgressNow(updatedWorkoutData);
    
    // Store reference to the set we just completed (for RPE retroactive save during rest)
    lastCompletedSetRef.current = { exerciseIndex: currentExerciseIndex, setIndex: completedSetIndex };
    setLastCompletedSetRpe(newSet.rpe);
    
    console.log(`[SET_SAVE] Set ${currentSet} saved: exercise="${targetExercise.exercise_name}" (${targetExercise.exercise_id}), index=${currentExerciseIndex}, setIndex=${completedSetIndex}, weight=${newSet.weight}, reps=${newSet.reps}, rpe=${newSet.rpe}`);

    // REAL-TIME PR CELEBRATION — compare this set against the exercise's
    // historical best (from last-sessions hint). Fire a non-blocking toast when
    // the client sets a new record (heavier, or same weight with more reps).
    (() => {
      const exId = targetExercise.exercise_id;
      const histPr = lastSessionsHints?.[exId]?.pr;
      // Baseline to beat: the higher of the historical PR and any mark already
      // celebrated this session for this exercise.
      const already = celebratedPrRef.current[exId] || null;
      const baseW = Math.max(histPr?.weight || 0, already?.weight || 0);
      const baseR = (already && already.weight === baseW) ? already.reps
                    : (histPr && histPr.weight === baseW ? histPr.reps : 0);
      const w = newSet.weight || 0;
      const r = newSet.reps || 0;
      // Only celebrate against a real historical reference (avoid firing on the
      // very first time an exercise is ever performed).
      const hasReference = !!histPr || !!already;
      const exName = targetExercise.exercise_name_es || targetExercise.exercise_name;
      const fireCelebration = (payload) => {
        setPrCelebration({ exerciseName: exName, setNumber: completedSetIndex + 1, ...payload });
        if (prCelebrationTimerRef.current) clearTimeout(prCelebrationTimerRef.current);
        prCelebrationTimerRef.current = setTimeout(() => setPrCelebration(null), 4000);
      };

      if (w > 0) {
        // Weighted PR — heavier, or same weight with more reps
        const beatsRecord = hasReference && (w > baseW || (w === baseW && r > baseR));
        if (beatsRecord) {
          celebratedPrRef.current[exId] = { weight: w, reps: r };
          fireCelebration({ weight: w, reps: r, bodyweight: false });
        }
      } else if (r > 0) {
        // Bodyweight PR — more reps than the best weight-less reference (0 kg is valid)
        const hasBwRef = (histPr && (histPr.weight || 0) === 0) || (already && (already.weight || 0) === 0);
        const bwBaseR = Math.max(
          (histPr && (histPr.weight || 0) === 0) ? (histPr.reps || 0) : 0,
          (already && (already.weight || 0) === 0) ? (already.reps || 0) : 0
        );
        if (hasBwRef && r > bwBaseR) {
          celebratedPrRef.current[exId] = { weight: 0, reps: r };
          fireCelebration({ weight: 0, reps: r, bodyweight: true });
        }
      }
    })();

    // SUPERSET LOGIC: Check if current exercise is part of a superset
    const currentExerciseData = workout.exercises[currentExerciseIndex];
    const nextExerciseData = workout.exercises[currentExerciseIndex + 1];
    const prevExerciseData = workout.exercises[currentExerciseIndex - 1];
    
    const isCurrentSuperset = currentExerciseData?.is_superset === true;
    const isNextSuperset = nextExerciseData?.is_superset === true;
    const isPrevSuperset = prevExerciseData?.is_superset === true;
    
    // Determine if we're in a superset pair
    const isInSupersetPair = (isCurrentSuperset && isNextSuperset) || (isCurrentSuperset && isPrevSuperset);
    
    console.log(`📊 Set ${currentSet} Complete: ${currentExercise.exercise_name}`, {
      currentIndex: currentExerciseIndex,
      isCurrentSuperset,
      isNextSuperset,
      isPrevSuperset,
      isInSupersetPair,
      supersetState
    });
    
    // Support both legacy (sets) and new (target_sets) field names
    const targetSets = currentExercise.target_sets || currentExercise.sets || 4;
    
    // Check if ALL SETS of current exercise are complete (data-driven, not counter-based)
    const completedSetsCount = updatedWorkoutData[currentExerciseIndex].actual_sets.length;
    if (completedSetsCount >= targetSets) {
      console.log('✅ ALL SETS COMPLETE FOR THIS EXERCISE!');
      
      // Mark exercise as completed
      updatedWorkoutData[currentExerciseIndex].completed = true;
      setWorkoutData(updatedWorkoutData);
      
      // Check if this exercise is part of a superset group
      const currentExercise = workout.exercises[currentExerciseIndex];
      const nextExercise = workout.exercises[currentExerciseIndex + 1];
      const currentGroup = currentExercise?.superset_group || 'A';
      const nextGroup = nextExercise?.superset_group || 'A';
      
      const isInSupersetGroup = currentExercise?.is_superset && 
                                nextExercise?.is_superset && 
                                currentGroup === nextGroup;
      
      if (isInSupersetGroup) {
        // We're in a superset group and there's more exercises in this group
        // Move to next exercise WITHOUT resetting currentSet (stay on same set number)
        console.log(`🔗 Continuing superset group "${currentGroup}": Moving to`, nextExercise.exercise_name, 'on Set', currentSet);
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        // DO NOT reset currentSet - keep same set number!
        
        // NOTE: Don't reset setData here - the exercise change effect will handle it
        // This ensures weight is set from client history, not empty
        
        setIsResting(false);
        setRestTimer(0);
        return;
      }
      
      // NOT in superset group OR last exercise in group - REST FIRST, THEN move to next exercise
      if (currentExerciseIndex < workout.exercises.length - 1) {
        // CRITICAL FIX: Start rest timer BEFORE moving to next exercise
        const restTime = parseInt(setData.rest_time) || 120;
        setRestTimer(0);
        setRestTarget(restTime);
        restJustEndedRef.current = false;
        setIsResting(true);
        setCurrentRestStartTime(new Date());
        currentRestStartTimeRef.current = new Date();
        console.log(`⏱️ Rest after completing exercise "${currentExercise.exercise_name}":`, restTime, 'seconds (target)');
        
        // Queue the navigation to happen after rest completes
        setPendingNavigation({
          type: 'next_exercise',
          nextIndex: currentExerciseIndex + 1
        });
        
        // NOTE: Don't reset setData here - the exercise change effect will handle it
        // Keep only the rest_time for timer purposes
      } else {
        // All exercises completed - PART 3 FIX: Check if last set needs RPE before showing rating modal
        const totalDuration = Math.floor((new Date() - workoutStartTime) / 1000);
        const clampedRestTime = Math.min(totalRestTime, totalDuration);
        const effectiveTrainingTime = Math.max(0, totalDuration - clampedRestTime);
        
        // Store finish data
        const finishData = {
          totalDuration,
          totalRestTime: clampedRestTime,
          effectiveTrainingTime
        };
        setPendingFinishData(finishData);
        
        // Check if last set has RPE
        const lastExerciseData = updatedWorkoutData[currentExerciseIndex];
        const lastSetIndex = lastExerciseData.actual_sets.length - 1;
        const lastSet = lastExerciseData.actual_sets[lastSetIndex];
        
        if (!lastSet?.rpe) {
          // STEP 1: Show last set RPE modal first (technical RPE for final set)
          setPendingLastSetRpe({
            exerciseIndex: currentExerciseIndex,
            setIndex: lastSetIndex,
            exerciseName: currentExercise.exercise_name_es || currentExercise.exercise_name
          });
          setShowLastSetRpeModal(true);
        } else {
          // RPE already recorded, go to STEP 2: Closing notice
          setShowClosingNoticeModal(true);
        }
        return;
      }
    } else {
      // More sets remaining in current exercise
      
      // SUPERSET LOGIC using groups:
      // Find the start of the current superset group by looking backwards
      const findSupersetGroupStart = (startIndex) => {
        const currentGroup = workout.exercises[startIndex]?.superset_group || 'A';
        let groupStart = startIndex;
        // Walk backwards while exercises have the same superset_group
        while (groupStart > 0 && 
               workout.exercises[groupStart - 1]?.is_superset && 
               workout.exercises[groupStart - 1]?.superset_group === currentGroup) {
          groupStart--;
        }
        return groupStart;
      };
      
      // Check if current and next exercises are in the same superset group
      const currentGroup = workout.exercises[currentExerciseIndex]?.superset_group || 'A';
      const nextGroup = workout.exercises[currentExerciseIndex + 1]?.superset_group || 'A';
      const prevGroup = workout.exercises[currentExerciseIndex - 1]?.superset_group || 'A';
      
      const isInSupersetGroup = isCurrentSuperset && isNextSuperset && currentGroup === nextGroup;
      const isEndOfSupersetGroup = isCurrentSuperset && isPrevSuperset && currentGroup === prevGroup && 
                                   (!isNextSuperset || (isNextSuperset && currentGroup !== nextGroup));
      const isStartOfNewGroup = isCurrentSuperset && (!isPrevSuperset || prevGroup !== currentGroup) && 
                                isNextSuperset && currentGroup === nextGroup;
      
      console.log('🔗 Superset Group Check:', {
        currentIndex: currentExerciseIndex,
        currentName: currentExerciseData?.exercise_name,
        currentGroup,
        isCurrentSuperset,
        nextName: nextExerciseData?.exercise_name,
        nextGroup,
        isNextSuperset,
        isInSupersetGroup,
        isEndOfSupersetGroup,
        isStartOfNewGroup,
        supersetState
      });
      
      if (isStartOfNewGroup) {
        // Starting a NEW superset group - initialize state
        console.log(`🔗 STARTING NEW SUPERSET GROUP "${currentGroup}" at index`, currentExerciseIndex);
        setSupersetState({
          isInSuperset: true,
          firstExerciseIndex: currentExerciseIndex,
          secondExerciseIndex: currentExerciseIndex + 1,
          onSecondExercise: true,
          currentGroup: currentGroup
        });
        
        // Move to next exercise immediately (no rest)
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        
        // Reset form for next exercise
        setSetData({
          reps: '',
          weight: '',
          rest_time: '120',
          rpe: ''
        });
        
        setIsResting(false);
        setRestTimer(0);
        
      } else if (isInSupersetGroup && supersetState.isInSuperset && supersetState.currentGroup === currentGroup) {
        // Continue within existing superset group (no rest)
        console.log(`⚡ SUPERSET GROUP "${currentGroup}": Continuing to`, nextExerciseData?.exercise_name, '- Set', currentSet);
        
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        
        // Reset form for next exercise
        setSetData({
          reps: '',
          weight: '',
          rest_time: '120',
          rpe: ''
        });
        
        // NO rest timer - move immediately
        setIsResting(false);
        setRestTimer(0);
        
      } else if (isEndOfSupersetGroup && supersetState.isInSuperset && supersetState.currentGroup === currentGroup) {
        // End of superset group - return to FIRST exercise of THIS group and REST
        const groupStart = findSupersetGroupStart(currentExerciseIndex);
        console.log(`⚡ SUPERSET GROUP "${currentGroup}" COMPLETE: Returning to`, workout.exercises[groupStart]?.exercise_name, '- Next Set', currentSet + 1);
        
        // Update superset state with the correct group start
        setCurrentExerciseIndex(groupStart);
        // currentSet synced by useEffect on currentExerciseIndex change
        setSupersetState({
          isInSuperset: false,  // Reset - will reinitialize when starting next set
          firstExerciseIndex: null,
          secondExerciseIndex: null,
          onSecondExercise: false,
          currentGroup: null
        });
        
        // Reset form
        setSetData({
          reps: '',
          weight: '',
          rest_time: '120',
          rpe: ''
        });
        
        // START REST TIMER after completing superset group
        const restTime = parseInt(setData.rest_time) || 120;
        setRestTimer(0);
        setRestTarget(restTime);
        restJustEndedRef.current = false;
        setIsResting(true);
        setCurrentRestStartTime(new Date());
        currentRestStartTimeRef.current = new Date();
        console.log(`⏱️ Rest after superset group "${currentGroup}":`, restTime, 'seconds (target)');
        
      } else {
        // NORMAL: Stay on current exercise, move to next set
        console.log('➕ Normal progression: Set', completedSetsCount + 1);
        setCurrentSet(completedSetsCount + 1);
        
        // Start rest timer between sets
        const restTime = parseInt(setData.rest_time) || 120;
        setRestTimer(0);
        setRestTarget(restTime);
        restJustEndedRef.current = false;
        setIsResting(true);
        setCurrentRestStartTime(new Date());
        currentRestStartTimeRef.current = new Date();
      }
    }

    // Reset form for next set.
    //   - Priority order for weight:
    //       1. The weight ACTUALLY USED in the previous set (if any).
    //          Once the client has lifted, their real choice is authoritative —
    //          we never re-suggest the template number after they've deviated.
    //       2. The per-set plan for set N (template hint, only useful for set 1).
    //       3. Whatever is currently in `setData.weight` (fallback for edge cases).
    //   - For rest_time, the per-set plan stays authoritative when present,
    //     so trainers can prescribe progressive rest changes via the template.
    //   - For reps the placeholder comes from resolveSetTargets via SetTable's
    //     `targetReps` prop; the input itself is intentionally cleared.
    const nextSetIdx = (workoutData[currentExerciseIndex]?.actual_sets?.length) || 0;
    const nextPlan = resolveSetTargets(workoutData[currentExerciseIndex], nextSetIdx);
    const hasPerSetPlan = Array.isArray(workoutData[currentExerciseIndex]?.target_set_plan)
      && workoutData[currentExerciseIndex].target_set_plan.length > nextSetIdx;
    const lastActualSet = nextSetIdx > 0
      ? workoutData[currentExerciseIndex]?.actual_sets?.[nextSetIdx - 1]
      : null;
    const inheritedWeight =
      lastActualSet?.weight != null && lastActualSet.weight !== ''
        ? String(lastActualSet.weight)
        : (hasPerSetPlan && nextPlan.weight != null
            ? String(nextPlan.weight)
            : setData.weight);
    setSetData({
      reps: '',
      weight: inheritedWeight,
      rest_time: hasPerSetPlan && nextPlan.rest_time != null ? String(nextPlan.rest_time) : setData.rest_time,
      rpe: ''
    });
  }, [setData, workoutData, currentExerciseIndex, workout, isCoachMode, lastSessionsHints, setWorkoutData, setCurrentExerciseIndex, setCurrentSet, supersetState, setIsResting, setRestTimer, setPendingNavigation, showLastSetRpeModal, setPendingLastSetRpe, setShowLastSetRpeModal, saveProgressNow]);

  const handleSkipRest = () => {
    // Track skipped rest time using ref to match timer effect
    if (currentRestStartTimeRef.current) {
      const actualRestTime = Math.floor((new Date() - currentRestStartTimeRef.current) / 1000);
      setTotalRestTime(prevTotal => prevTotal + actualRestTime);
      currentRestStartTimeRef.current = null;
      setCurrentRestStartTime(null);
    }
    setRestTimer(0);
    restJustEndedRef.current = false;
    setIsResting(false);
  };
  
  // RPE FIX: Retroactively save RPE to the LAST completed set (not the next one)
  // ARCHITECTURE: RPE updates the recommended target ONLY (restTarget) — the
  // running elapsed timer (restTimer) is never reset or modified by RPE.
  //   RPE 1–4   → 60 s
  //   RPE 5–8   → 120 s
  //   RPE 9–10  → 180 s   (3-minute cap; was 240s — corrected per UX spec)
  const [restAutoHint, setRestAutoHint] = useState(false);
  const restAutoHintTimerRef = useRef(null);

  const suggestRestForRpe = (rpe) => {
    if (rpe == null) return null;
    if (rpe <= 4) return 60;
    if (rpe <= 8) return 120;
    return 180;
  };

  const handleRestRpeSelect = useCallback((rpeValue) => {
    const { exerciseIndex, setIndex } = lastCompletedSetRef.current;
    if (exerciseIndex === null || setIndex === null) return;
    
    console.log(`[RPE] Retroactive save: rpe=${rpeValue} → exerciseIndex=${exerciseIndex}, setIndex=${setIndex}`);
    
    const updatedWorkoutData = [...workoutData];
    if (updatedWorkoutData[exerciseIndex]?.actual_sets?.[setIndex]) {
      updatedWorkoutData[exerciseIndex].actual_sets[setIndex].rpe = rpeValue;
      // Persist the suggested rest into the just-completed set's rest_time
      // (this is the TARGET applied for that set's rest period — informational)
      const suggested = suggestRestForRpe(rpeValue);
      if (suggested) {
        updatedWorkoutData[exerciseIndex].actual_sets[setIndex].rest_time = suggested;
      }
      setWorkoutData(updatedWorkoutData);
      setLastCompletedSetRpe(rpeValue);

      // CRITICAL: persist immediately so the Live Board picks up the new target
      // on its next 2-second poll. Without this, the debounced autosave can lag
      // several seconds — long enough for the trainer to notice divergence.
      saveProgressNow(updatedWorkoutData);

      // Update RECOMMENDED target only — never touch the elapsed timer.
      if (suggested) {
        // Default for the NEXT set's rest target
        setSetData((prev) => ({ ...prev, rest_time: String(suggested) }));
        // If currently resting, update the live target (recommendation).
        // The elapsed timer keeps running; bell + auto-end will fire when
        // elapsed >= new target (handled by the dedicated effect).
        if (isResting) {
          setRestTarget(suggested);
        }
        // Show the hint pulse for 4s
        setRestAutoHint(true);
        if (restAutoHintTimerRef.current) clearTimeout(restAutoHintTimerRef.current);
        restAutoHintTimerRef.current = setTimeout(() => setRestAutoHint(false), 4000);
      }
    }
  }, [workoutData, setWorkoutData, isResting, saveProgressNow]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Estimated time-to-finish (adaptive): scale the planned remaining time by the
  // client's actual pace so far. Recomputes as sets are logged / exercise changes.
  const etaRemaining = useMemo(() => {
    const list = workoutData && workoutData.length ? workoutData : (workout.exercises || []);
    if (!list.length) return null;
    const plannedTotal = estimateWorkoutSeconds(list);
    const completedInCurrent = list[currentExerciseIndex]?.actual_sets?.length || 0;
    const plannedRemaining = estimateRemainingSeconds(list, currentExerciseIndex, completedInCurrent);
    const plannedCompleted = Math.max(0, plannedTotal - plannedRemaining);
    const workingSeconds = Math.max(0, elapsedTime - totalRestTime);
    return adaptiveRemainingSeconds(plannedRemaining, plannedCompleted, workingSeconds);
  }, [workoutData, workout.exercises, currentExerciseIndex, elapsedTime, totalRestTime]);

  
  // Format time to nearest 0.5 minute for metrics
  const formatTimeToHalfMinutes = (seconds) => {
    const totalMinutes = seconds / 60;
    const rounded = Math.round(totalMinutes * 2) / 2; // Round to nearest 0.5
    return rounded;
  };

  const handlePreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
      // currentSet synced by useEffect on currentExerciseIndex change
    }
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < workout.exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      // currentSet synced by useEffect on currentExerciseIndex change
    }
  };

  return (
    <div className="space-y-6">
      {/* P0.1 Recovery toast — auto-hides after restoring an unsynced draft */}
      {showRecoveryToast && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[99999] bg-blue-900/95 border border-blue-400/40 text-blue-50 text-sm px-4 py-2.5 rounded-lg shadow-lg backdrop-blur-sm"
          data-testid="draft-recovery-toast"
        >
          ✓ Se recuperó el progreso de esta sesión
        </div>
      )}
      {/* Remote coach edit: manual-apply banner (no jumpy auto-refresh) */}
      {coachUpdateAvailable && !applyingCoachUpdate && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-3 bg-orange-600/95 border border-orange-300/50 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg backdrop-blur-sm"
          data-testid="coach-update-banner"
        >
          <span>{coachUpdateIsExec ? '✏️ Tu entrenador corrigió tus valores' : '🔄 Tu entrenador ha actualizado tu rutina'}</span>
          <button
            onClick={applyCoachUpdate}
            className="font-semibold bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md transition"
            data-testid="coach-update-apply-btn"
          >
            Actualizar
          </button>
        </div>
      )}
      {applyingCoachUpdate && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[99999] bg-orange-600/95 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg" data-testid="coach-update-applying">
          Actualizando rutina…
        </div>
      )}
      {/* Coach Mode: Header changes to dark blue when active */}
      <div className={`glass-card p-4 transition-all duration-300 ${
        isCoachMode 
          ? 'bg-gradient-to-r from-blue-900 to-indigo-900 border-l-4 border-blue-400' 
          : ''
      }`}>
        <div className="flex flex-wrap justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-white">🏋️ Entrenamiento activo</h1>
              
              {/* Coach Mode Badge */}
              {isCoachMode && (
                <span 
                  className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse"
                  data-testid="coach-mode-badge"
                >
                  🛡️ Modo Entrenador Activo
                </span>
              )}
              
              {/* Sync Status Indicator (P0.1) — subtle; shows nothing when synced */}
              <div className="flex items-center gap-2" data-testid="autosave-indicator">
                {isSaving ? (
                  <span className="flex items-center gap-1 text-yellow-300 text-sm animate-pulse" data-testid="sync-status-saving">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Guardando...
                  </span>
                ) : syncState === 'pending' ? (
                  <span className="flex items-center gap-1.5 text-amber-300/80 text-xs" data-testid="sync-status-pending">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80"></span>
                    Pendiente de sincronizar
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-1">
              <div className="text-blue-200">
                ⏱️ Transcurrido: <span className="font-semibold text-white" data-testid="elapsed-time-value">{formatTime(elapsedTime)}</span>
              </div>
              <div className="text-blue-200">
                💤 Descanso: <span className="font-semibold text-white">{formatTime(totalRestTime)}</span>
              </div>
              <div className="text-green-200">
                💪 Entrenando: <span className="font-semibold text-white">{formatTime(Math.max(0, elapsedTime - totalRestTime))}</span>
              </div>
              {etaRemaining != null && (
                <div className="text-amber-200" data-testid="workout-eta">
                  🏁 Faltan aprox.: <span className="font-semibold text-white">{formatDuration(etaRemaining)}</span>
                  <span className="text-amber-200/70" data-testid="workout-eta-clock"> ({new Date(Date.now() + etaRemaining * 1000).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })})</span>
                </div>
              )}
            </div>
            <p className="text-blue-200 mt-1">
              Ejercicio {currentExerciseIndex + 1} de {workout.exercises.length}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center justify-end">
            {/* Coach Mode Button */}
            {!isCoachMode ? (
              <button 
                onClick={() => setShowCoachModeAuth(true)} 
                className="bg-blue-700/50 hover:bg-blue-600/70 text-blue-200 hover:text-white px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                data-testid="coach-mode-btn"
                title="Activar Modo Entrenador para modificar estructura"
              >
                🛡️ Modo Entrenador
              </button>
            ) : (
              <button 
                onClick={() => setIsCoachMode(false)} 
                className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                data-testid="deactivate-coach-mode-btn"
              >
                ✓ Desactivar Modo
              </button>
            )}
            <button onClick={handleCancelWorkoutClick} className="btn-secondary" data-testid="cancel-workout-btn">
              ❌ Cancelar
            </button>
            <button onClick={handleEarlyFinishClick} className="btn-primary" data-testid="early-finish-btn">
              ✅ Terminar antes
            </button>
          </div>
        </div>
        
        {/* Coach Mode Error Message */}
        {coachModeError && (
          <div className="mt-3 bg-red-900/30 border border-red-500/50 text-red-200 px-4 py-2 rounded-lg text-sm">
            ⚠️ {coachModeError}
            <button 
              onClick={() => setCoachModeError(null)}
              className="ml-2 text-red-300 hover:text-white"
            >
              ✕
            </button>
          </div>
        )}
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-blue-900/30 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${isCoachMode ? 'bg-blue-400' : 'bg-blue-500'}`}
              style={{ width: `${((currentExerciseIndex + 1) / workout.exercises.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
      
      {/* Coach Mode: Add Exercise Button (visible in Coach Mode) */}
      {isCoachMode && (
        <div className="flex justify-end gap-2 -mt-2">
          <button
            onClick={handleCoachApplyToTemplate}
            className="bg-indigo-600/50 hover:bg-indigo-500/70 text-indigo-200 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            data-testid="apply-to-template-btn"
          >
            📋 Aplicar a Plantilla
          </button>
          <button
            onClick={() => openExercisePicker('add')}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            data-testid="add-exercise-btn"
          >
            + Añadir Ejercicio
          </button>
        </div>
      )}
      
      {/* Exercise List - Reorderable */}
      <div className="glass-card p-4 mb-4">
        <details className="group">
          <summary className="cursor-pointer list-none flex items-center justify-between text-blue-200 hover:text-blue-100">
            <span className="font-semibold">📋 Lista de ejercicios ({workout.exercises.length})</span>
            <span className="text-blue-400 text-sm group-open:hidden">▼ Clic para {isCoachMode ? 'ver/editar' : 'ver'}</span>
            <span className="text-blue-400 text-sm hidden group-open:block">▲ Ocultar</span>
          </summary>
          
          <div className="mt-4 space-y-2">
            <p className="text-blue-300 text-sm mb-3">
              {isCoachMode 
                ? '🛡️ Modo Entrenador: Arrastra para reordenar, o usa los botones para editar' 
                : '💡 Arrastra para reordenar los ejercicios'}
            </p>
            {workout.exercises.map((exercise, idx) => (
              <div
                key={idx}
                onDragOver={(e) => {
                  if (!isCoachMode) return; // Only allow drag in coach mode
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderColor = isCoachMode ? '#3b82f6' : 'transparent';
                }}
                onDrop={(e) => {
                  if (!isCoachMode) return; // Only allow drop in coach mode
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.style.borderColor = '#3b82f6';
                  
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  const toIndex = idx;
                  
                  if (!isNaN(fromIndex) && fromIndex !== toIndex) {
                    // Snapshot previous state so we can revert if the API call fails.
                    const prevExercises = workout.exercises;
                    const prevWorkoutData = workoutData;
                    const prevCurrentIndex = currentExerciseIndex;

                    // Create new exercises array with reordered items
                    const newExercises = [...workout.exercises];
                    const [movedExercise] = newExercises.splice(fromIndex, 1);
                    newExercises.splice(toIndex, 0, movedExercise);
                    
                    // Update workoutData array to reflect the change
                    const updatedWorkoutData = workoutData.map((data, i) => {
                      // Copy exercise order to workoutData
                      if (i < newExercises.length) {
                        const ex = newExercises[i];
                        return {
                          ...data,
                          exercise_id: ex.exercise_id,
                          exercise_name: ex.exercise_name,
                          target_sets: ex.target_sets,
                          target_reps: ex.target_reps,
                          target_weight: ex.target_weight
                        };
                      }
                      return data;
                    });
                    
                    setWorkoutData(updatedWorkoutData);
                    
                    // Adjust current exercise index if needed
                    let nextCurrentIndex = currentExerciseIndex;
                    if (currentExerciseIndex === fromIndex) {
                      nextCurrentIndex = toIndex;
                    } else if (fromIndex < currentExerciseIndex && toIndex >= currentExerciseIndex) {
                      nextCurrentIndex = currentExerciseIndex - 1;
                    } else if (fromIndex > currentExerciseIndex && toIndex <= currentExerciseIndex) {
                      nextCurrentIndex = currentExerciseIndex + 1;
                    }
                    if (nextCurrentIndex !== currentExerciseIndex) {
                      setCurrentExerciseIndex(nextCurrentIndex);
                    }
                    
                    // Update workout exercises in place (needed for UI)
                    workout.exercises = newExercises;

                    // BUGFIX (Live Board order mismatch): Persist the new order
                    // to the backend so save-progress doesn't overlay actual_sets
                    // onto the OLD positional slot.
                    const exerciseOrder = Array.from({ length: prevExercises.length }, (_, i) => i);
                    const [movedIdx] = exerciseOrder.splice(fromIndex, 1);
                    exerciseOrder.splice(toIndex, 0, movedIdx);
                    coachModeAxios(
                      'put',
                      `${API}/workouts/${workout.id}/coach/reorder-exercises`,
                      { exercise_order: exerciseOrder }
                    ).then(() => {
                      console.log(`[REORDER-DND] Persisted ${fromIndex} -> ${toIndex} (order=${JSON.stringify(exerciseOrder)})`);
                    }).catch((err) => {
                      console.error('[REORDER-DND] Failed to persist new order, reverting:', err);
                      // Revert local state so the frontend stays consistent with the DB
                      workout.exercises = prevExercises;
                      setWorkoutData(prevWorkoutData);
                      if (nextCurrentIndex !== prevCurrentIndex) {
                        setCurrentExerciseIndex(prevCurrentIndex);
                      }
                      setCoachModeError(err.response?.data?.detail || 'No se pudo reordenar el ejercicio');
                    });
                  }
                }}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  idx === currentExerciseIndex 
                    ? 'bg-blue-500/30 border-blue-400' 
                    : isCoachMode 
                      ? 'bg-blue-900/20 border-l-4 border-l-blue-500 border-transparent hover:border-blue-500/50'
                      : 'bg-blue-900/20 border-transparent hover:border-blue-500/50'
                }`}
              >
                {/* Drag Handle - Only visible/draggable in Coach Mode */}
                {isCoachMode && (
                  <div
                    draggable="true"
                    onDragStart={(e) => {
                      e.stopPropagation();
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', idx.toString());
                      // Safely set opacity with null check
                      const parent = e.currentTarget?.parentElement;
                      if (parent) {
                        setTimeout(() => { parent.style.opacity = '0.5'; }, 0);
                      }
                    }}
                    onDragEnd={(e) => {
                      e.stopPropagation();
                      // Safely restore opacity with null check
                      const parent = e.currentTarget?.parentElement;
                      if (parent) {
                        parent.style.opacity = '1';
                      }
                    }}
                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-blue-500/20 rounded"
                    title="Arrastrar para reordenar"
                  >
                    <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                    </svg>
                  </div>
                )}
                
                {/* Exercise Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${idx === currentExerciseIndex ? 'text-blue-200' : 'text-blue-400'}`}>
                      #{idx + 1}
                    </span>
                    <span className={`font-semibold ${idx === currentExerciseIndex ? 'text-white' : 'text-blue-200'}`}>
                      {exercise.exercise_name_es || exercise.exercise_name}
                    </span>
                    {exercise.is_superset && (
                      <span className="text-xs bg-orange-500/30 text-orange-200 px-2 py-0.5 rounded">SUPERSET</span>
                    )}
                    {idx === currentExerciseIndex && (
                      <span className="text-xs bg-green-500/30 text-green-200 px-2 py-0.5 rounded">← actual</span>
                    )}
                  </div>
                  {isCoachMode ? (
                    <div
                      className="flex items-center gap-1.5 mt-1 text-xs text-blue-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="number"
                        min="1"
                        inputMode="numeric"
                        key={`ts-${idx}-${exercise.exercise_id}`}
                        defaultValue={exercise.target_sets || exercise.sets || 3}
                        onBlur={(e) => handleCoachUpdateTargets(idx, { target_sets: parseInt(e.target.value) || 1 })}
                        className="w-11 px-1 py-0.5 rounded bg-blue-900/40 border border-blue-500/30 text-white text-center text-base"
                        data-testid={`edit-target-sets-${idx}`}
                      />
                      <span>series ×</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        key={`tr-${idx}-${exercise.exercise_id}`}
                        defaultValue={exercise.target_reps || exercise.reps || 10}
                        onBlur={(e) => handleCoachUpdateTargets(idx, { target_reps: parseInt(e.target.value) || 0 })}
                        className="w-11 px-1 py-0.5 rounded bg-blue-900/40 border border-blue-500/30 text-white text-center text-base"
                        data-testid={`edit-target-reps-${idx}`}
                      />
                      <span>reps @</span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        inputMode="decimal"
                        key={`tw-${idx}-${exercise.exercise_id}`}
                        defaultValue={exercise.target_weight || exercise.weight || 0}
                        onBlur={(e) => handleCoachUpdateTargets(idx, { target_weight: parseFloat(e.target.value) || 0 })}
                        className="w-14 px-1 py-0.5 rounded bg-blue-900/40 border border-blue-500/30 text-white text-center text-base"
                        data-testid={`edit-target-weight-${idx}`}
                      />
                      <span>kg</span>
                    </div>
                  ) : (
                    <div className="text-xs text-blue-400 mt-1">
                      {exercise.target_sets || exercise.sets || 3} sets × {exercise.target_reps || exercise.reps || 10} reps @ {exercise.target_weight || exercise.weight || 0} kg
                    </div>
                  )}
                </div>
                
                {/* Coach Mode: Reorder, Replace & Remove Buttons */}
                {isCoachMode && (
                  <div className="flex gap-1 items-center">
                    {/* Move Up/Down - Touch-friendly reorder */}
                    <div className="flex flex-col gap-0.5 mr-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMoveExercise(idx, -1); }}
                        disabled={idx === 0}
                        className={`px-1.5 py-0.5 rounded text-xs transition-all ${idx === 0 ? 'opacity-30 cursor-not-allowed' : 'bg-blue-600/50 hover:bg-blue-500/70 text-blue-200 hover:text-white'}`}
                        data-testid={`move-up-exercise-${idx}`}
                        title="Mover arriba"
                      >
                        ▲
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMoveExercise(idx, 1); }}
                        disabled={idx === workout.exercises.length - 1}
                        className={`px-1.5 py-0.5 rounded text-xs transition-all ${idx === workout.exercises.length - 1 ? 'opacity-30 cursor-not-allowed' : 'bg-blue-600/50 hover:bg-blue-500/70 text-blue-200 hover:text-white'}`}
                        data-testid={`move-down-exercise-${idx}`}
                        title="Mover abajo"
                      >
                        ▼
                      </button>
                    </div>
                    <button
                      onClick={() => openExercisePicker('replace', idx)}
                      className="bg-blue-600/50 hover:bg-blue-500/70 text-blue-200 hover:text-white px-2 py-1 rounded text-xs transition-all"
                      data-testid={`replace-exercise-${idx}`}
                      title="Reemplazar ejercicio"
                    >
                      🔄
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleSuperset(idx); }}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        exercise.is_superset
                          ? 'bg-orange-500/30 text-orange-200 hover:bg-orange-500/40'
                          : 'bg-blue-600/50 hover:bg-blue-500/70 text-blue-200 hover:text-white'
                      }`}
                      data-testid={`toggle-superset-${idx}`}
                      title="Superset"
                    >
                      {exercise.is_superset ? 'SS' : '+SS'}
                    </button>
                    {exercise.is_superset && (
                      <select
                        value={exercise.superset_group || 'A'}
                        onChange={(e) => { e.stopPropagation(); handleUpdateSupersetGroup(idx, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className="px-1.5 py-1 rounded text-xs bg-orange-900/30 text-orange-200 border border-orange-500/30"
                        data-testid={`superset-group-${idx}`}
                      >
                        {['A', 'B', 'C', 'D', 'E'].map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={() => handleCoachRemoveExercise(idx)}
                      className="bg-blue-600/50 hover:bg-blue-500/70 text-blue-200 hover:text-white px-2 py-1 rounded text-xs transition-all"
                      data-testid={`remove-exercise-${idx}`}
                      title="Eliminar ejercicio"
                    >
                      🗑️
                    </button>
                  </div>
                )}
                
                {/* Jump to exercise button */}
                {idx !== currentExerciseIndex && (
                  <button
                    onClick={() => setCurrentExerciseIndex(idx)}
                    className="btn-secondary text-xs py-1 px-3"
                    data-testid={`jump-to-exercise-${idx}`}
                  >
                    Ir →
                  </button>
                )}
              </div>
            ))}
          </div>
        </details>
      </div>

      {/* Current Exercise */}
      <div className="glass-card p-6">
        {/* Superset Indicator */}
        {currentExercise.is_superset && (
          <div className="bg-gradient-to-r from-orange-500/30 to-red-500/30 border-2 border-orange-400 p-4 rounded-lg mb-6 animate-pulse">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="text-2xl font-bold text-orange-200">
                  🔗 MODO SUPERSERIE 🔗
                </div>
                <div className="bg-orange-500 text-white text-2xl font-black px-4 py-2 rounded-lg shadow-lg">
                  {currentExercise.superset_group || 'A'}
                </div>
              </div>
              <div className="text-orange-300">
                Completa este ejercicio, luego pasa directamente al siguiente
              </div>
              <div className="text-orange-400 text-sm mt-1">
                ⚡ Sin descanso entre ejercicios de superserie ⚡
              </div>
            </div>
          </div>
        )}
        
        {/* Replay provenance chip — surfaces only when this workout was
            created via POST /workouts/{id}/replay. */}
        {workout?.replayed_from_workout_id && (
          <div
            className="mb-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-purple-500/10 border border-purple-400/30 text-purple-200 text-xs"
            data-testid="replay-provenance-chip"
            title={`Repetición de la sesión original (${workout.replayed_from_workout_id.slice(0, 8)})`}
          >
            <span>↻</span>
            <span>Repetición de sesión anterior</span>
          </div>
        )}

        {/* Exercise Header — consolidated compact header (image + actions on
            the left; title + Última sesión / Mejor marca / Sugerido on the
            right). Replaces the old triple stack of separate cards. */}
        {!isCardioExercise && (() => {
          const hint = lastSessionsHints?.[currentExercise.exercise_id];
          const lastSession = hint?.sessions?.find((s) => !s.is_deload) || hint?.sessions?.[0] || null;
          const pr = hint?.pr || null;
          return (
            <ExerciseHeader
              key={`hdr-${currentExercise.exercise_id}-${currentExerciseIndex}`}
              imageUrl={displayImageUrl}
              name={currentExercise.exercise_name_es || currentExercise.exercise_name}
              trainerNotes={currentExercise.note || currentExercise.notes}
              hasInstructions={!!fullExerciseData}
              onOpenDetails={() => setShowExerciseDetails(true)}
              feedback={exerciseFeedback[currentExercise.exercise_id] || null}
              onToggleFeedback={(next) => setExerciseFeedback({
                ...exerciseFeedback,
                [currentExercise.exercise_id]: next,
              })}
              lastSession={lastSession}
              pr={pr}
              onOpenWorkout={handleOpenDayWorkout}
              onOpenProgress={() => setShowProgress(true)}
            />
          );
        })()}

        {/* Cardio mode keeps its own minimal header (no last-session anchor) */}
        {isCardioExercise && (
          <ExerciseHeader
            key={`hdr-cardio-${currentExercise.exercise_id}-${currentExerciseIndex}`}
            imageUrl={displayImageUrl}
            name={currentExercise.exercise_name_es || currentExercise.exercise_name}
            trainerNotes={currentExercise.note || currentExercise.notes}
            hasInstructions={!!fullExerciseData}
            onOpenDetails={() => setShowExerciseDetails(true)}
            feedback={exerciseFeedback[currentExercise.exercise_id] || null}
            onToggleFeedback={(next) => setExerciseFeedback({
              ...exerciseFeedback,
              [currentExercise.exercise_id]: next,
            })}
          />
        )}

        {isCardioExercise && (
          /* CARDIO MODE: Timer-focused badges (unchanged — redesign targets strength flow only) */
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="cardio-badges">
            <div className="badge bg-cyan-500/20 text-cyan-200 text-center border border-cyan-500/30">
              <div className="text-xs opacity-75">Ronda</div>
              <div className="text-lg font-bold">{(workoutData[currentExerciseIndex]?.actual_sets?.length || 0) + (isCardioTimerRunning || cardioTimer > 0 ? 1 : 0)} / {currentExercise.target_sets || currentExercise.sets || 4}</div>
            </div>
            <div className="badge bg-teal-500/20 text-teal-200 text-center border border-teal-500/30">
              <div className="text-xs opacity-75">Tiempo objetivo</div>
              <div className="text-lg font-bold">{cardioTargetTime > 0 ? formatCardioTime(cardioTargetTime) : 'Manual'}</div>
            </div>
            <div className="badge bg-orange-500/20 text-orange-200 text-center border border-orange-500/30">
              <div className="text-xs opacity-75">Descanso</div>
              <div className="text-lg font-bold">{currentExercise.rest_time || 60}s</div>
            </div>
          </div>
        )}

        {/* Rest Timer — REAL elapsed stopwatch (counts up from 0). RPE only
            updates the recommended target shown below — it never resets the
            elapsed timer. */}
        {isResting && (() => {
          const restColor = getRestTimeColor(restTarget);
          const colorClasses = getRestColorClasses(restColor);
          const selectedRpeLabel = getRpeLabel(lastCompletedSetRpe);
          const overTarget = restTarget > 0 && restTimer >= restTarget;
          const progressPct = restTarget > 0
            ? Math.min(100, (restTimer / restTarget) * 100)
            : 0;
          
          return (
            <div className={`${colorClasses.bg} border-2 ${colorClasses.border} rounded-lg p-6 mb-6 text-center`}>
              <div className="mb-1">
                <span className={`${colorClasses.text} font-semibold text-lg tracking-wide uppercase text-xs`}>
                  ⏱️ Descanso actual
                </span>
              </div>
              {/* BIG elapsed timer — never resets on RPE */}
              <div
                className={`text-6xl font-bold ${colorClasses.timer} mb-2 tabular-nums ${overTarget ? '' : 'animate-pulse'}`}
                data-testid="rest-elapsed-timer"
              >
                {formatTime(restTimer)}
              </div>
              {/* Recommended target (informational, RPE-influenced) */}
              <div className="mb-3 text-sm text-white/70" data-testid="rest-target-label">
                {restAutoHint && lastCompletedSetRpe ? (
                  <span className="text-amber-300 italic">
                    Objetivo recomendado: <span className="font-semibold">{formatTime(restTarget)}</span>
                    <span className="opacity-80"> (según RPE {lastCompletedSetRpe})</span>
                  </span>
                ) : (
                  <span>
                    Objetivo recomendado: <span className="font-semibold">{formatTime(restTarget)}</span>
                  </span>
                )}
                {overTarget && (
                  <span className="ml-2 text-green-300 font-semibold">· objetivo cumplido ✓</span>
                )}
              </div>
              <div className={`w-full bg-black/30 rounded-full h-3 mb-4 overflow-hidden`}>
                <div 
                  className={`${colorClasses.progress} h-3 rounded-full transition-all duration-1000`}
                  style={{ width: `${progressPct}%` }}
                ></div>
              </div>
              
              {/* PART 1: RPE Input — saves to LAST COMPLETED SET (not next set) */}
              <div className={`${colorClasses.bg} rounded-lg p-4 mb-4`}>
                <p className={`${colorClasses.text} text-sm font-medium mb-3`}>
                  💪 ¿Cómo de dura fue esa serie?
                </p>
                
                {/* RPE Number Scale */}
                <div className="flex justify-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rpe) => {
                    const rpeLabel = getRpeLabel(rpe);
                    return (
                      <button
                        key={rpe}
                        onClick={() => handleRestRpeSelect(rpe)}
                        data-testid={`rest-rpe-${rpe}`}
                        className={`w-9 h-11 rounded text-sm font-bold transition-all ${
                          lastCompletedSetRpe === rpe
                            ? 'ring-2 ring-white scale-110 shadow-lg'
                            : ''
                        } ${
                          rpeLabel.color === 'green' ? 'bg-green-500 text-white hover:bg-green-400' :
                          rpeLabel.color === 'yellow' ? 'bg-yellow-500 text-white hover:bg-yellow-400' :
                          rpeLabel.color === 'orange' ? 'bg-orange-500 text-white hover:bg-orange-400' :
                          'bg-red-500 text-white hover:bg-red-400'
                        }`}
                      >
                        {rpe}
                      </button>
                    );
                  })}
                </div>
                
                {/* PART 1: Dynamic feedback text based on selection */}
                {selectedRpeLabel ? (
                  <div className="mt-3 p-3 rounded-lg bg-black/20">
                    <p className={`text-lg font-bold ${
                      selectedRpeLabel.color === 'green' ? 'text-green-300' :
                      selectedRpeLabel.color === 'yellow' ? 'text-yellow-300' :
                      selectedRpeLabel.color === 'orange' ? 'text-orange-300' :
                      'text-red-300'
                    }`}>
                      {selectedRpeLabel.text}
                    </p>
                    <p className="text-white/70 text-sm mt-1">
                      {selectedRpeLabel.helper}
                    </p>
                  </div>
                ) : (
                  <p className={`${colorClasses.text} text-sm opacity-70`}>
                    Selecciona un número para valorar tu esfuerzo
                  </p>
                )}
              </div>

              {/* Manual override of recommended target — never resets elapsed */}
              <div className="mb-3" data-testid="rest-target-override">
                <p className="text-white/60 text-[11px] uppercase tracking-widest mb-2">
                  Ajustar objetivo
                </p>
                <div className="flex justify-center gap-1.5 flex-wrap">
                  {[60, 90, 120, 150, 180].map((sec) => {
                    const active = restTarget === sec;
                    const label = sec < 60 ? `${sec}s` : sec === 60 ? '1:00' : sec === 90 ? '1:30' : sec === 120 ? '2:00' : sec === 150 ? '2:30' : '3:00';
                    return (
                      <button
                        key={sec}
                        type="button"
                        onClick={() => {
                          // Update the live target (used by the elapsed >= target check)
                          setRestTarget(sec);
                          // Also persist to the last completed set's rest_time so the
                          // Live Board (and any other consumer reading actual_sets[i].rest_time)
                          // reflects this override. Without this, intra shows e.g. 1:30
                          // while the Live Board still shows 2:00 from the RPE-derived value.
                          const { exerciseIndex, setIndex } = lastCompletedSetRef.current;
                          console.log(`[OVERRIDE-PILL] sec=${sec} → exerciseIndex=${exerciseIndex}, setIndex=${setIndex}`);
                          if (exerciseIndex !== null && setIndex !== null) {
                            const updated = [...workoutData];
                            if (updated[exerciseIndex]?.actual_sets?.[setIndex]) {
                              updated[exerciseIndex].actual_sets[setIndex].rest_time = sec;
                              setWorkoutData(updated);
                              saveProgressNow(updated);
                              console.log(`[OVERRIDE-PILL] Persisted rest_time=${sec} on set ${setIndex} of ex ${exerciseIndex}`);
                            } else {
                              console.warn('[OVERRIDE-PILL] Target set not found in workoutData — skipping persist');
                            }
                          } else {
                            console.warn('[OVERRIDE-PILL] lastCompletedSetRef is null — cannot persist target');
                          }
                          // And update the default for the NEXT set's plan
                          setSetData((prev) => ({ ...prev, rest_time: String(sec) }));
                        }}
                        data-testid={`rest-target-${sec}`}
                        className={`px-3 py-1 rounded-full text-xs tabular-nums transition ${
                          active
                            ? 'bg-white text-slate-900 font-semibold shadow'
                            : 'bg-white/10 text-white/70 hover:bg-white/20'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <button
                onClick={handleSkipRest}
                className="btn-secondary"
              >
                Saltar descanso ⏭️
              </button>
              <button
                onClick={handleTestSound}
                className="mt-2 text-xs text-white/50 hover:text-white/80 underline transition-colors"
                data-testid="test-sound-btn"
              >
                {audioEnabled
                  ? `🔔 Probar sonido · ${getTimerSoundName(soundIndex, language)}`
                  : '🔇 Activar sonido'}
              </button>
              <p className={`${colorClasses.text} text-sm mt-2 opacity-70`}>
                {pendingNavigation ? 'Siguiente ejercicio tras el descanso' : 'La siguiente serie estará lista cuando termine el temporizador'}
              </p>
            </div>
          );
        })()}

        {/* Set Input Form — CONDITIONAL: Cardio vs Strength */}
        {isCardioExercise ? (
          /* =============================================
             CARDIO MODE: Timer-based interface
             ============================================= */
          <div className="mb-6" data-testid="cardio-timer-section">
            {/* Main Timer Display */}
            <div className={`rounded-2xl p-8 mb-5 text-center transition-all ${
              cardioTargetHit
                ? 'bg-gradient-to-br from-green-900/60 to-emerald-900/60 border-2 border-green-400 shadow-lg shadow-green-500/20'
                : isCardioTimerRunning 
                ? 'bg-gradient-to-br from-cyan-900/60 to-teal-900/60 border-2 border-cyan-400 shadow-lg shadow-cyan-500/20' 
                : cardioTimer > 0 
                  ? 'bg-gradient-to-br from-teal-900/40 to-cyan-900/40 border-2 border-teal-400/50' 
                  : 'bg-gradient-to-br from-gray-800/60 to-gray-900/60 border-2 border-gray-600/30'
            }`}>
              <div className={`text-sm font-medium mb-2 tracking-wider uppercase ${cardioTargetHit ? 'text-green-300' : 'text-cyan-300/80'}`}>
                {cardioPreCountdown !== null ? '¡Prepárate!' : cardioTargetHit ? '¡Serie completada!' : isCardioTimerRunning ? (cardioTargetTime > 0 ? 'Cuenta atrás' : 'En marcha') : cardioTimer > 0 ? 'Pausado' : (cardioTargetTime > 0 ? 'Preparado' : 'Cronómetro')}
              </div>
              <div className={`font-mono font-black tracking-tight mb-2 transition-all ${
                cardioPreCountdown !== null
                  ? 'text-8xl text-amber-300 animate-pulse'
                  : cardioTargetHit
                  ? 'text-7xl text-green-300 animate-pulse'
                  : isCardioTimerRunning 
                  ? 'text-7xl text-cyan-100 animate-pulse' 
                  : cardioTimer > 0 
                    ? 'text-7xl text-teal-200' 
                    : 'text-7xl text-gray-400'
              }`} data-testid="cardio-timer-display">
                {cardioPreCountdown !== null
                  ? cardioPreCountdown
                  : formatCardioTime(cardioTargetTime > 0 ? Math.max(0, cardioTargetTime - cardioTimer) : cardioTimer)}
              </div>

              {/* Sub-line: for a timed set show the plan + elapsed; manual shows nothing */}
              {cardioTargetTime > 0 && cardioPreCountdown === null && (
                <div className="text-xs text-cyan-300/60 mb-3" data-testid="cardio-timer-subline">
                  Objetivo {formatCardioTime(cardioTargetTime)} · Transcurrido {formatCardioTime(cardioTimer)}
                </div>
              )}
              
              {/* Timer progress vs target */}
              {cardioTargetTime > 0 && (
                <div className="w-full bg-gray-700/50 rounded-full h-2 mb-4 max-w-xs mx-auto">
                  <div 
                    className={`h-2 rounded-full transition-all duration-1000 ${
                      cardioTimer >= cardioTargetTime ? 'bg-green-400' : 'bg-cyan-400'
                    }`}
                    style={{ width: `${Math.min(100, (cardioTimer / cardioTargetTime) * 100)}%` }}
                  ></div>
                </div>
              )}
              
              {/* Timer Controls */}
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleCardioToggleTimer}
                  disabled={isResting}
                  className={`px-8 py-3 rounded-xl text-lg font-bold transition-all select-none ${
                    isResting ? 'bg-gray-600 text-gray-400 cursor-not-allowed' :
                    isCardioTimerRunning
                      ? 'bg-yellow-500 hover:bg-yellow-400 text-yellow-950 shadow-lg shadow-yellow-500/30'
                      : 'bg-cyan-500 hover:bg-cyan-400 text-cyan-950 shadow-lg shadow-cyan-500/30'
                  }`}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  data-testid="cardio-start-pause-btn"
                >
                  {isResting ? 'Descansando...' : cardioPreCountdown !== null ? `Cancelar (${cardioPreCountdown})` : isCardioTimerRunning ? '⏸ Pausar' : '▶ Iniciar'}
                </button>
                <button
                  onClick={handleCardioResetTimer}
                  disabled={cardioTimer === 0 || isCardioTimerRunning}
                  className="px-5 py-3 rounded-xl text-lg font-bold bg-gray-700 hover:bg-gray-600 text-gray-200 transition-all disabled:opacity-30 disabled:cursor-not-allowed select-none"
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  data-testid="cardio-reset-btn"
                >
                  ↺ Reset
                </button>
              </div>
            </div>
            
            {/* Target Time Quick Buttons — presets or Manual (open stopwatch) */}
            <div className="mb-4">
              <label className="block text-cyan-300 text-sm font-medium mb-2">
                Tiempo objetivo <span className="text-cyan-300/50 font-normal">— la serie se detiene sola al llegar; «Manual» = cronómetro libre</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 60, label: '1 min' },
                  { value: 120, label: '2 min' },
                  { value: 180, label: '3 min' },
                  { value: 300, label: '5 min' },
                  { value: 600, label: '10 min' },
                  { value: 0, label: 'Manual' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setCardioTargetTime(opt.value)}
                    disabled={isCardioTimerRunning || cardioPreCountdown !== null}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      cardioTargetTime === opt.value
                        ? 'bg-cyan-500 text-white ring-2 ring-cyan-300 shadow-md'
                        : 'bg-cyan-900/30 text-cyan-300 hover:bg-cyan-800/40'
                    }`}
                    data-testid={`cardio-target-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Rest Time Selection (same as strength) */}
            <div className="mb-4">
              <label className="block text-blue-200 text-sm font-medium mb-2">Descanso entre rondas</label>
              <div className="flex gap-2">
                {[
                  { value: '30', label: '30s', color: 'green' },
                  { value: '60', label: '1min', color: 'green' },
                  { value: '90', label: '1.5min', color: 'orange' },
                  { value: '120', label: '2min', color: 'orange' },
                  { value: '180', label: '3min', color: 'red' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSetData({...setData, rest_time: opt.value})}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      setData.rest_time === opt.value
                        ? opt.color === 'green' ? 'bg-green-500 text-white ring-2 ring-green-300' :
                          opt.color === 'orange' ? 'bg-orange-500 text-white ring-2 ring-orange-300' :
                          'bg-red-500 text-white ring-2 ring-red-300'
                        : opt.color === 'green' ? 'bg-green-900/30 text-green-300 hover:bg-green-800/40' :
                          opt.color === 'orange' ? 'bg-orange-900/30 text-orange-300 hover:bg-orange-800/40' :
                          'bg-red-900/30 text-red-300 hover:bg-red-800/40'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* PART 4: Exercise Notes/Observations */}
            <div>
              <details className="group">
                <summary className="cursor-pointer list-none flex items-center justify-between text-blue-200 text-sm font-medium mb-2">
                  <span>Observaciones del ejercicio</span>
                  <span className="text-blue-400 text-xs group-open:hidden">Añadir nota</span>
                  <span className="text-blue-400 text-xs hidden group-open:block">Ocultar</span>
                </summary>
                <textarea
                  value={exerciseNotes[currentExerciseIndex] || ''}
                  onChange={(e) => {
                    const newNotes = { ...exerciseNotes, [currentExerciseIndex]: e.target.value };
                    setExerciseNotes(newNotes);
                    const updatedWorkoutData = [...workoutData];
                    if (updatedWorkoutData[currentExerciseIndex]) {
                      updatedWorkoutData[currentExerciseIndex].note = e.target.value;
                      setWorkoutData(updatedWorkoutData);
                    }
                  }}
                  className="input-field w-full h-20 text-sm resize-none"
                  placeholder="Observaciones sobre este ejercicio..."
                />
              </details>
            </div>
          </div>
        ) : (
          /* =============================================
             STRENGTH MODE: Unified Set Table (single source of truth)
             - The "Completar serie X" button is rendered INSIDE the active
               row of SetTable so the action feels physically attached to it.
             - One-tap "Repetir última" prefills the active set with the
               matching set from the client's previous session.
             ============================================= */
          <>
            {(() => {
              const curSets = workoutData[currentExerciseIndex]?.actual_sets || [];
              // Repeat-last-session prefill for the ACTIVE set: reuse the
              // matching set from the previous (non-deload) session; fall back
              // to that session's last logged set if it had fewer sets.
              const rlHint = lastSessionsHints?.[currentExercise.exercise_id];
              const rlSession = rlHint?.sessions?.find((s) => !s.is_deload) || rlHint?.sessions?.[0] || null;
              const rlSets = rlSession?.sets || [];
              const rlPrev = rlSets.length > 0 ? (rlSets[curSets.length] || rlSets[rlSets.length - 1]) : null;
              const repeatLastSet = rlPrev ? { reps: rlPrev.reps, weight: rlPrev.weight } : null;

              return (
                <SetTable
                  targetSets={currentExercise.target_sets || currentExercise.sets || (currentExercise.target_set_plan?.length || 3)}
                  targetReps={resolveSetTargets(currentExercise, curSets.length).reps}
                  targetWeight={resolveSetTargets(currentExercise, curSets.length).weight}
                  actualSets={curSets}
                  setData={setData}
                  onChangeSetData={setSetData}
                  isResting={isResting}
                  onComplete={handleCompleteSet}
                  completeLabel="Completar serie"
                  completeDisabled={!setData.reps || supersetGate.blocked}
                  restAutoHint={restAutoHint}
                  repeatLastSet={repeatLastSet}
                  onRepeatLast={repeatLastSet ? () => setSetData((prev) => ({
                    ...prev,
                    reps: repeatLastSet.reps != null ? String(repeatLastSet.reps) : prev.reps,
                    weight: (repeatLastSet.weight != null && repeatLastSet.weight !== '') ? String(repeatLastSet.weight) : prev.weight,
                  })) : null}
                  isCoachMode={isCoachMode}
                  onEditDoneSet={(setIdx, patch) => handleEditDoneSet(currentExerciseIndex, setIdx, patch)}
                  exerciseKey={`${currentExerciseIndex}-${currentExercise?.exercise_id || currentExercise?.exercise_name || ''}`}
                />
              );
            })()}

            {/* Superset sequential gate hint (clients only) */}
            {supersetGate.blocked && (
              <div
                className="mb-4 -mt-2 flex items-center gap-2 rounded-lg bg-amber-900/30 border border-amber-700/40 px-3 py-2 text-amber-300 text-sm"
                data-testid="superset-gate-hint"
              >
                <span className="flex-shrink-0">🔗</span>
                <span>Completa la serie {supersetGate.requiredSet} de {supersetGate.prevName} primero</span>
              </div>
            )}

            {/* Per-exercise observations (kept, demoted) */}
            <div className="mb-4">
              <details className="group">
                <summary className="cursor-pointer list-none flex items-center justify-between text-slate-400 text-xs font-medium mb-2">
                  <span>Observaciones del ejercicio</span>
                  <span className="text-slate-500 text-xs group-open:hidden">Añadir nota</span>
                  <span className="text-slate-500 text-xs hidden group-open:block">Ocultar</span>
                </summary>
                <textarea
                  value={exerciseNotes[currentExerciseIndex] || ''}
                  onChange={(e) => {
                    const newNotes = { ...exerciseNotes, [currentExerciseIndex]: e.target.value };
                    setExerciseNotes(newNotes);
                    const updatedWorkoutData = [...workoutData];
                    if (updatedWorkoutData[currentExerciseIndex]) {
                      updatedWorkoutData[currentExerciseIndex].note = e.target.value;
                      setWorkoutData(updatedWorkoutData);
                    }
                  }}
                  className="input-field w-full h-20 text-sm resize-none"
                  placeholder="Sensaciones, dolor, ajustes técnicos..."
                  data-testid="intra-exercise-notes"
                />
              </details>
            </div>
          </>
        )}

        {/* Action Buttons — CONDITIONAL: Cardio vs Strength */}
        <div className="space-y-3">
          {isCardioExercise ? (
            /* CARDIO: Complete round button */
            <button
              onClick={handleCardioCompleteRound}
              disabled={cardioTimer === 0 || isResting || isCardioTimerRunning || supersetGate.blocked}
              className={`w-full text-lg font-bold py-4 rounded-xl select-none transition-all ${
                isResting 
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : isCardioTimerRunning
                    ? 'bg-yellow-600/50 text-yellow-300 cursor-not-allowed'
                    : supersetGate.blocked
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : cardioTargetHit
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white shadow-lg shadow-green-500/40 animate-pulse'
                        : cardioTimer > 0
                          ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white shadow-lg shadow-cyan-500/30'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="complete-round-btn"
            >
              {isResting 
                ? `Descansando... ${formatTime(restTimer)}`
                : supersetGate.blocked
                  ? `Completa ${supersetGate.prevName} primero`
                  : isCardioTimerRunning 
                    ? 'Pausa el timer para guardar ronda'
                    : cardioTargetHit
                      ? `✓ Completar serie (${formatCardioTime(cardioTimer)})`
                      : cardioTimer > 0 
                        ? (cardioTargetTime > 0 ? `Terminar antes (${formatCardioTime(cardioTimer)})` : `Guardar ronda (${formatCardioTime(cardioTimer)})`)
                        : 'Inicia el timer para comenzar'
              }
            </button>
          ) : null /* STRENGTH: Complete-set button is now rendered inside SetTable's active row */}
          
          <div className="flex space-x-2">
            <button
              onClick={handlePreviousExercise}
              disabled={currentExerciseIndex === 0}
              className="btn-secondary flex-1 select-none"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="prev-exercise-btn"
            >
              ⬅️ Ejercicio anterior
            </button>
            <button
              onClick={handleNextExercise}
              disabled={currentExerciseIndex === workout.exercises.length - 1}
              className="btn-secondary flex-1 select-none"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="next-exercise-btn"
            >
              Siguiente ejercicio ➡️
            </button>
          </div>
        </div>

        {/* Exercise Instructions */}
        {currentExercise.instructions && (
          <div className="mt-6 bg-blue-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-200 mb-2">Instrucciones</h4>
            <p className="text-blue-300 text-sm">{currentExercise.instructions}</p>
          </div>
        )}
      </div>

      {/* Completed Rounds Summary — CARDIO ONLY (strength uses SetTable as source of truth) */}
      {isCardioExercise && workoutData[currentExerciseIndex]?.actual_sets?.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="font-semibold text-white mb-3">
            Rondas completadas
          </h3>
          <div className="space-y-2">
            {workoutData[currentExerciseIndex].actual_sets.map((set, index) => (
              <div key={index} className="flex justify-between items-center bg-blue-900/20 p-2 rounded">
                <span className="text-blue-200 text-sm">
                  Ronda {index + 1}
                </span>
                <div className="text-right">
                  <span className="text-white font-medium" data-testid={`cardio-round-${index}-time`}>
                    {formatCardioTime(set.time || 0)}
                  </span>
                  {set.rpe && (
                    <span className="text-blue-300 text-xs ml-2">RPE: {set.rpe}</span>
                  )}
                </div>
              </div>
            ))}
            {workoutData[currentExerciseIndex].actual_sets.length > 1 && (
              <div className="flex justify-between items-center bg-cyan-900/30 p-2 rounded mt-2 border border-cyan-500/20">
                <span className="text-cyan-300 text-sm font-semibold">Total</span>
                <span className="text-cyan-100 font-bold" data-testid="cardio-total-time">
                  {formatCardioTime(workoutData[currentExerciseIndex].actual_sets.reduce((sum, s) => sum + (s.time || 0), 0))}
                  <span className="text-cyan-300 text-xs ml-2">
                    ({workoutData[currentExerciseIndex].actual_sets.length} rondas)
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Exercise Details Modal */}
      {showExerciseDetails && fullExerciseData && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" onClick={() => setShowExerciseDetails(false)}>
          <div className="glass-card p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">{currentExercise.exercise_name_es || currentExercise.exercise_name}</h3>
                <div className="flex flex-wrap gap-2">
                  {fullExerciseData.category && (
                    <span className="badge bg-blue-500/30 text-blue-200 text-xs">
                      {fullExerciseData.category === 'strength' ? 'Fuerza' :
                       fullExerciseData.category === 'cardio' ? 'Cardio' :
                       fullExerciseData.category === 'flexibility' ? 'Flexibilidad' :
                       fullExerciseData.category === 'balance' ? 'Equilibrio' :
                       fullExerciseData.category}
                    </span>
                  )}
                  {fullExerciseData.difficulty && (
                    <span className={`badge text-xs ${
                      fullExerciseData.difficulty === 'beginner' ? 'bg-green-500/30 text-green-200' :
                      fullExerciseData.difficulty === 'intermediate' ? 'bg-yellow-500/30 text-yellow-200' :
                      'bg-red-500/30 text-red-200'
                    }`}>
                      {fullExerciseData.difficulty === 'beginner' ? 'Principiante' :
                       fullExerciseData.difficulty === 'intermediate' ? 'Intermedio' :
                       fullExerciseData.difficulty === 'advanced' ? 'Avanzado' :
                       fullExerciseData.difficulty}
                    </span>
                  )}
                </div>
              </div>
              <button 
                onClick={() => setShowExerciseDetails(false)}
                className="text-white hover:text-red-300 text-3xl font-bold leading-none"
              >
                ✕
              </button>
            </div>

            {/* Exercise GIF/Image */}
            {fullExerciseData.image_url && (
              <div className="mb-6 text-center">
                <img 
                  src={fullExerciseData.image_url} 
                  alt={currentExercise.exercise_name}
                  className="w-full max-w-md mx-auto rounded-lg border-2 border-blue-400/30"
                />
              </div>
            )}

            {/* Workout Target */}
            <div className="glass-card p-4 mb-4 bg-blue-900/30">
              <h4 className="text-blue-200 font-semibold mb-2">🎯 Objetivo</h4>
              <p className="text-white text-lg">
                {currentExercise.target_sets || currentExercise.sets || 3} series × {currentExercise.target_reps || currentExercise.reps || 12} reps @ {currentExercise.target_weight || currentExercise.weight || 0}kg
              </p>
            </div>

            {/* Muscle Groups */}
            {fullExerciseData.muscle_groups && fullExerciseData.muscle_groups.length > 0 && (
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-blue-200 mb-2">💪 Músculos trabajados</h4>
                <div className="flex flex-wrap gap-2">
                  {fullExerciseData.muscle_groups.map((muscle, idx) => (
                    <span key={idx} className="bg-purple-500/30 text-purple-200 px-3 py-1 rounded text-sm">
                      {translateMuscle(muscle)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Target/Synergist/Stabilizer Muscles */}
            {(fullExerciseData.target_muscles || fullExerciseData.synergist_muscles || fullExerciseData.stabilizer_muscles) && (
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-blue-200 mb-2">🎯 Desglose muscular detallado</h4>
                <div className="space-y-2">
                  {fullExerciseData.target_muscles && fullExerciseData.target_muscles.length > 0 && (
                    <div>
                      <p className="text-blue-300 text-sm font-semibold">Músculos objetivo:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {fullExerciseData.target_muscles.map((muscle, idx) => (
                          <span key={idx} className="bg-red-500/30 text-red-200 px-2 py-1 rounded text-xs">
                            {translateMuscle(muscle)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {fullExerciseData.synergist_muscles && fullExerciseData.synergist_muscles.length > 0 && (
                    <div>
                      <p className="text-blue-300 text-sm font-semibold">Músculos sinergistas:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {fullExerciseData.synergist_muscles.map((muscle, idx) => (
                          <span key={idx} className="bg-orange-500/30 text-orange-200 px-2 py-1 rounded text-xs">
                            {translateMuscle(muscle)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {fullExerciseData.stabilizer_muscles && fullExerciseData.stabilizer_muscles.length > 0 && (
                    <div>
                      <p className="text-blue-300 text-sm font-semibold">Músculos estabilizadores:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {fullExerciseData.stabilizer_muscles.map((muscle, idx) => (
                          <span key={idx} className="bg-yellow-500/30 text-yellow-200 px-2 py-1 rounded text-xs">
                            {translateMuscle(muscle)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Execution Steps */}
            {fullExerciseData.execution_steps && fullExerciseData.execution_steps.length > 0 && (
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-blue-200 mb-2">📋 Pasos de ejecución</h4>
                <div className="glass-card p-4 bg-blue-900/20">
                  <ul className="space-y-2">
                    {(language === 'es' && fullExerciseData.execution_steps_es 
                      ? fullExerciseData.execution_steps_es 
                      : fullExerciseData.execution_steps
                    ).map((step, idx) => (
                      <li key={idx} className="text-blue-100 text-sm leading-relaxed">
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Form Tips */}
            {fullExerciseData.form_tips && fullExerciseData.form_tips.length > 0 && (
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-green-200 mb-2">✅ Consejos de técnica</h4>
                <div className="glass-card p-4 bg-green-900/20">
                  <ul className="space-y-2">
                    {fullExerciseData.form_tips.map((tip, idx) => (
                      <li key={idx} className="text-green-100 text-sm flex items-start">
                        <span className="mr-2">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Common Mistakes */}
            {fullExerciseData.common_mistakes && fullExerciseData.common_mistakes.length > 0 && (
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-red-200 mb-2">⚠️ Errores comunes</h4>
                <div className="glass-card p-4 bg-red-900/20">
                  <ul className="space-y-2">
                    {fullExerciseData.common_mistakes.map((mistake, idx) => (
                      <li key={idx} className="text-red-100 text-sm flex items-start">
                        <span className="mr-2">✗</span>
                        <span>{mistake}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Benefits */}
            {fullExerciseData.benefits && fullExerciseData.benefits.length > 0 && (
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-yellow-200 mb-2">🎯 Beneficios</h4>
                <div className="glass-card p-4 bg-yellow-900/20">
                  <ul className="space-y-2">
                    {fullExerciseData.benefits.map((benefit, idx) => (
                      <li key={idx} className="text-yellow-100 text-sm flex items-start">
                        <span className="mr-2">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Basic Instructions (fallback if no enhanced data) */}
            {!fullExerciseData.execution_steps && fullExerciseData.instructions && (
              <div className="glass-card p-4 mb-4">
                <h4 className="text-blue-200 font-semibold mb-3">📋 Instrucciones</h4>
                <p className="text-white whitespace-pre-line leading-relaxed text-sm">
                  {language === 'es' && fullExerciseData.instructions_es 
                    ? fullExerciseData.instructions_es 
                    : fullExerciseData.instructions}
                </p>
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setShowExerciseDetails(false)}
              className="btn-primary w-full"
            >
              ✅ Cerrar
            </button>
          </div>
        </div>
      )}

      {/* =============================================
           3-STEP SESSION COMPLETION FLOW
           Step 1: Last Set RPE (technical)
           Step 2: Closing Notice (informational)
           Step 3: Global Session Rating (experience-based)
         ============================================= */}
      
      {/* STEP 1: Last Set RPE Modal - Technical RPE for final set */}
      {showLastSetRpeModal && pendingLastSetRpe && (
        <LastSetRpeModal
          exerciseName={pendingLastSetRpe.exerciseName}
          onSubmit={(rpe) => {
            // Save RPE to the last set
            const updatedWorkoutData = [...workoutData];
            const { exerciseIndex, setIndex } = pendingLastSetRpe;
            if (updatedWorkoutData[exerciseIndex]?.actual_sets?.[setIndex]) {
              updatedWorkoutData[exerciseIndex].actual_sets[setIndex].rpe = rpe;
              setWorkoutData(updatedWorkoutData);
            }
            
            // Close this modal and go to STEP 2: Closing Notice
            setShowLastSetRpeModal(false);
            setPendingLastSetRpe(null);
            setShowClosingNoticeModal(true);
          }}
        />
      )}

      {/* STEP 2: Session Closing Notice Modal */}
      {showClosingNoticeModal && (
        <SessionClosingNoticeModal
          onContinue={() => {
            // Close this modal and go to STEP 3: Session Rating
            setShowClosingNoticeModal(false);
            setShowSessionRatingModal(true);
          }}
        />
      )}

      {/* Early-finish confirmation — keeps the survey path explicit while
          guarding against accidental clicks on "Terminar antes". */}
      {showEarlyFinishConfirm && (
        <div
          className="fixed inset-0 bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          style={{ zIndex: 99999 }}
          data-testid="early-finish-confirm-modal"
        >
          <div className="glass-card p-6 sm:p-8 max-w-md w-full text-center max-h-[90dvh] overflow-y-auto my-auto">
            <div className="text-5xl mb-4">⏹️</div>
            <h2 className="text-xl font-bold text-white mb-3">
              ¿Terminar el entrenamiento ahora?
            </h2>
            <p className="text-blue-200 text-sm mb-6">
              Cerrarás la sesión con las series registradas hasta este momento.
              Te pediremos un RPE final y tu valoración global antes de guardarla.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEarlyFinishConfirm(false)}
                className="btn-secondary flex-1"
                data-testid="early-finish-cancel-btn"
              >
                Volver al entrenamiento
              </button>
              <button
                onClick={confirmEarlyFinish}
                className="btn-primary flex-1"
                data-testid="early-finish-confirm-btn"
              >
                Sí, terminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Global Session Rating Modal */}
      {showSessionRatingModal && (
        <SessionRatingModal
          sessionRating={sessionRating}
          setSessionRating={setSessionRating}
          sessionNotes={sessionNotes}
          setSessionNotes={setSessionNotes}
          exerciseFeedback={exerciseFeedback}
          onSubmit={async () => {
            // Calculate summary data from workout
            const totalSetsCompleted = workoutData.reduce((sum, ex) => {
              return sum + (ex.actual_sets?.length || 0);
            }, 0);
            
            const totalVolume = workoutData.reduce((sum, ex) => {
              if (!ex.actual_sets) return sum;
              return sum + ex.actual_sets.reduce((setSum, set) => {
                const weight = parseFloat(set.weight) || 0;
                const reps = parseInt(set.reps) || 0;
                return setSum + (weight * reps);
              }, 0);
            }, 0);
            
            // Count supersets - unique superset groups that have been executed
            const supersetGroups = new Set();
            workoutData.forEach(ex => {
              if (ex.is_superset && ex.superset_group && ex.actual_sets?.length > 0) {
                supersetGroups.add(ex.superset_group);
              }
            });
            
            // Detect PRs from backend (single source of truth)
            let prsAchieved = 0;
            try {
              const prResponse = await axios.get(`${API}/workouts/${workout.id}/detect-prs`, { headers: getAuthHeaders() });
              prsAchieved = prResponse.data?.prs?.length || 0;
              console.log('[PR] Detected PRs from backend:', prsAchieved);
            } catch (err) {
              console.error('[PR] Error detecting PRs:', err);
            }
            
            // Set summary data
            const summaryObj = {
              duration: pendingFinishData?.totalDuration || 0,
              effectiveTime: pendingFinishData?.effectiveTrainingTime ?? null,
              totalExercises: workoutData.length,
              totalSetsCompleted,
              totalVolume: Math.round(totalVolume),
              supersetsCompleted: supersetGroups.size,
              prsAchieved,
              exercises: workoutData,
              clientId: workout?.client_id,
              workoutName: workout?.template_name,
              session_notes: sessionNotes
            };
            setWorkoutSummaryData(summaryObj);

            // FINISH THE WORKOUT NOW (one step earlier) — persist on rating
            // submit so a client who reads the summary and just closes the
            // screen without tapping the final button still ends up with a
            // properly finished session. The summary becomes a read-only recap.
            const finished = await onFinishWorkout({
              ...pendingFinishData,
              session_rating: sessionRating,
              exercise_feedback: exerciseFeedback,
              session_notes: sessionNotes,
              summary: summaryObj,
              keepOpen: true,
            });
            if (finished === false) {
              // Persist failed (rare) — keep the rating modal so the client
              // can retry rather than silently leaving the session active.
              return;
            }

            // Only clear local buffers AFTER the finish is confirmed saved.
            setIsDirty(false);
            clearDraft(workout.id);
            clearStartAnchor(workout.id);

            // Close rating modal and show the (already-saved) summary recap
            setShowSessionRatingModal(false);
            setShowWorkoutSummary(true);
          }}
        />
      )}

      {/* STEP 4: Workout Summary Modal — read-only recap (already saved) */}
      {showWorkoutSummary && (
        <WorkoutSummaryModal
          summaryData={workoutSummaryData}
          onContinue={() => {
            setShowWorkoutSummary(false);
            onExit?.();
          }}
        />
      )}

      {/* P0: Exit Confirmation Modal - Data Loss Prevention */}
      {showExitConfirmation && (
        <ExitConfirmationModal
          onConfirm={handleConfirmExit}
          onCancel={handleCancelExit}
          language={language}
        />
      )}

      {/* Real-time PR celebration toast */}
      <PrCelebrationToast celebration={prCelebration} />

      {/* Coach Mode Authentication Modal */}
      <CoachModeAuthModal
        isOpen={showCoachModeAuth}
        onClose={() => setShowCoachModeAuth(false)}
        onSuccess={() => {
          setIsCoachMode(true);
          console.log('[CoachMode] Authentication successful');
        }}
        workoutId={workout.id}
        verifyPin={verifyCoachPin}
        activateCoachMode={activateCoachMode}
      />

      {/* Coach Mode: Full Exercise Selector (same as Planner) */}
      {showExercisePicker && isCoachMode && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-[99998]"
          onClick={handleCloseExercisePicker}
          data-testid="coach-exercise-selector-modal"
        >
          <div
            className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-xl"
            onClick={e => e.stopPropagation()}
          >
            <ExerciseSelectorGrid
              exercises={filteredExerciseCatalog}
              onSelectExercise={exercisePickerMode === 'add' ? handleCoachAddExercise : handleCoachReplaceExercise}
              onClose={handleCloseExercisePicker}
              isTemplateMode={false}
              exerciseUsage={clientExerciseUsage}
              selectedExercises={exercisePickerMode === 'add' ? (workout?.exercises || []) : null}
              t={t}
            />
          </div>
        </div>
      )}

      {/* "+ info" popup — finished-workout summary for a past PR / last session */}
      {dayWorkout && (
        <WorkoutDetailsModal
          workout={dayWorkout}
          language="es"
          onClose={() => setDayWorkout(null)}
        />
      )}

      {/* "Ver progreso" — full progression chart for the current exercise */}
      {showProgress && currentExercise?.exercise_id && workout?.client_id && (
        <ExerciseHistoryModal
          exerciseId={currentExercise.exercise_id}
          exerciseName={currentExercise.exercise_name_es || currentExercise.exercise_name}
          clientId={workout.client_id}
          subtitle="Tu progreso"
          lang="es"
          onClose={() => setShowProgress(false)}
        />
      )}
    </div>
  );
};

// Printable Workout Sheet Component

export default ActiveWorkoutInterface;
